/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 *                        💜 HEARTBEATS™ - MODULE PRINCIPAL
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Copyright (c) 2024-2025 Abdelghani Baaloulou - Tous droits réservés
 * 
 * PROPRIÉTAIRE: Abdelghani Baaloulou
 * SIRET: 428 992 796 00022 | TVA: FR54428992796
 * 
 * Ce fichier fait partie du logiciel Heartbeats™, une application de rencontres
 * de proximité. Tous les éléments (code, design, algorithmes, personnages IA)
 * sont la propriété exclusive de l'auteur.
 * 
 * ÉLÉMENTS PROTÉGÉS:
 * - Personnages: Luna™, Enzo™, Pixie™, Nyx™, Kurson™
 * - Algorithmes: Soul™, Psyche™, Destins Croisés™
 * - Fonctionnalités: Blind Date™, Shadow Mode™, HBT Tokens™
 * 
 * Toute reproduction, modification ou distribution non autorisée est interdite
 * et passible de poursuites (Art. L335-2 Code de la Propriété Intellectuelle).
 * 
 * Licence d'utilisation accordée à: VM MARKET (La Petite Épicerie)
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Flame, Heart, X, MessageCircle, Sparkles, MapPin, 
  Eye, EyeOff, Send, ChevronLeft, ChevronRight, Users, User,
  Shuffle, Gift, Star, Clock, Check, AlertTriangle,
  Wine, Leaf, Cigarette, Crown, Shield, Zap, Camera, Flag, Image,
  Mic, Square, Play, Pause, Volume2, Hand, Circle,
  Video, VideoOff, PhoneOff, Phone, MicOff, ShoppingBag, Palette, Wallet, Moon, Edit, Share2, TrendingUp,
  Ghost, Rocket, Settings
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { api } from '../services/api';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import axios from 'axios';
import Peer from 'simple-peer';
import { useSocket } from '../hooks/useSocket';
import { useHeartbeatsStore } from '../store/heartbeatsStore';
import AvatarCreator from './AvatarCreator';
import { GiftShop, MyGiftVouchers } from './PaymentShop';
import CrownShopStripe from './CrownShop';
import { PaywallModal, PremiumLock, PREMIUM_FEATURES } from './Paywall';

// 🧲 EXTRACTION INTELLIGENTE DU PRÉNOM
import { extractFirstName } from '../utils/positiveMessages';

// 💰 PROGRAMME AFFILIATION HEARTBEATS PREMIUM
import AffiliateDashboard from './heartbeats/AffiliateDashboard';

// 🎵 IMPORTS DEPUIS LE MODULE HEARTBEATS REFACTORISÉ
// Soul - Loi de l'Attraction
import { getGuideForUser, GuideQuickTip } from './heartbeats/soul';
import { BigFaceGuideChat } from './heartbeats/BigFaceLuna';
import { PassionSelector, SoulQuestionsWizard, calculateSoulCompatibility, PASSION_CATEGORIES, PASSIONS } from './heartbeats/soul';

// Shared - Composants partagés
import { ProfileFrame, FrameSelector, PROFILE_FRAMES } from './heartbeats/shared';
import { RoomsList, RoomCard, RoomChatView, THEMED_ROOMS } from './heartbeats/shared';

// HBT - Token Economy
import { WalletCard, HBTShopModal, GiftReceivedModal } from './heartbeats/hbt';

// Shadow - Les 7 Péchés Capitaux
import { ShadowMode, ModeToggle } from './heartbeats/shadow';

// Components - Panels et sous-composants (ex-heartbeats)
import {
  LeaderboardPanel,
  CrownBadge,
  GiftCatalogPanel,
  SuperLikeModal,
  OnlineUsersPanel,
  VoiceRoomsPanel,
  AtStorePanel,
  CrossingsPanel,
  CrossingsMap,
  ActiveVideoCallUI,
  VideoCallModal,
  IncomingCallModal,
  ProtectedImage,
  ProtectedPhotoModal,
  SpeedDatingPanel
} from './heartbeats/components';

// Views - Vues principales
import { MyProfileView, ProfilePreviewCard, calculateProfileCompletion } from './heartbeats/views';

// 💕 Couple Mode - Pour les couples qui cherchent des amis
import { RelationshipSelector, CoupleProfileSetup, RELATIONSHIP_STATUS, COUPLE_INTERESTS } from './heartbeats/CoupleMode';

// 📸 Stories - Contenu éphémère 24h "Je suis là"
import { StoriesFeed, StoryCreator, StoryViewer } from './heartbeats/features/stories';

// 🔥 Viral - Features pour faire buzzer l'app
import { HotOrNot, SecretCrush, ShareCard, InviteFriends } from './heartbeats/features/viral';

// 🎮 Games - Mini-jeux Dating
import { WouldYouRather, CompatibilityQuiz, GameLobby } from './heartbeats/features/games';

// 🙈 Blind Date - Matcher sans voir les photos
import { BlindDateCard, BlindDatePanel, RevealMoment } from './heartbeats/features/blind-date';

// 🚀 Wingman - Pixie la commandante de bord
import { PixieBubble, IcebreakersPanel } from './heartbeats/features/wingman';

// 🌟 Soul - Aspirations & Rêves
import { SoulAspirationsQuiz } from './heartbeats/features/soul';

// 🧠 Psyche - Quiz doux (sous-marin)
import { PsycheQuiz } from './heartbeats/features/psyche';

// 💫 Compatibility - Score enrichi
import { CompatibilityBadge, CompatibilityRing, CompatibilityCard, CompatibilityMini, useCompatibility } from './heartbeats/features/compatibility';

// 🎼 ARCHITECTURE MOZART/JOBS/TESLA - Nouveaux composants refactorisés
import { 
  GamesTab, 
  BlindDateTab, 
  SoulTab, 
  DiscoverTab, 
  MatchesTab, 
  ViralTab, 
  StoriesTab,
  LeaderboardTab,
  RoomsTab,
  OnlineTab,
  SpeedDatingTab,
  CouplesTab,
  CrushesTab,
  CrossingsTab,
  AtStoreTab,
  GuidesTab
} from './heartbeats/tabs';
import { TxtofsTab } from './heartbeats/tabs/TxtofsTab';
import { TxtofsGate } from './heartbeats/AttractionTestFlow';
import { BigFaceGuidesSelector } from './heartbeats/BigFaceGuides';
import { GuideIntelligenceProvider, useGuideIntelligence } from './heartbeats/soul/GuideIntelligence';
import { GuidedTour } from './heartbeats/soul/GuidedTour';
import ProfileSetupModal from './heartbeats/ProfileSetupModal';
import PassionsSetupModal from './heartbeats/PassionsSetupModal';
import { VerificationHub } from './heartbeats/features/verification/Verification';

// ⚖️ Documents légaux Heartbeats (CGU, Confidentialité) - Modals
import { HeartbeatsCGUModal, HeartbeatsPrivacyModal, HeartbeatsLegalLinks } from './heartbeats/HeartbeatsLegalModals';

// 🔐 Coffre Spatial Heartbeats - Système de déverrouillage sécurisé
import HeartbeatsSpaceVault from './heartbeats/HeartbeatsSpaceVault';

// 🗑️ Suppression de compte Heartbeats (RGPD) - UNIQUEMENT POUR HEARTBEATS
import DeleteAccountModal from './DeleteAccountModal';

import { useGames, useBlindDate, usePixie, useSoul } from '../hooks/heartbeats';
import ProactiveGuidePopup from './ProactiveGuidePopup';

// ============================================
// 🔗 UTILITAIRE : Liens cliquables
// ============================================
const LinkifyText = ({ text, isDarkMode }) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return (
    <>
      {parts.map((part, i) => {
        if (part.match(urlRegex)) {
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className={`underline hover:opacity-80 ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`}
              onClick={(e) => e.stopPropagation()}
            >
              {part.length > 40 ? part.substring(0, 40) + '...' : part}
            </a>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

// Configuration des affinités basées sur les cartes de fidélité
const AFFINITY_CONFIG = {
  alcohol: { icon: Wine, label: 'Amateur de bon vin', color: '#722F37', emoji: '🍷' },
  cbd: { icon: Leaf, label: 'Fan de CBD', color: '#2D5A45', emoji: '🌿' },
  puffs: { icon: Cigarette, label: 'Vapoteur cool', color: '#6366F1', emoji: '💨' }
};

// Icebreakers fun
const ICEBREAKERS = [
  { question: "Plutôt soirée Netflix ou sortie en ville ?", options: ["🎬 Netflix & Chill", "🎉 Sortie en ville"] },
  { question: "Ton mood du moment ?", options: ["😎 Tranquille", "🔥 On fire", "😴 Fatigué(e)", "🥳 Festif"] },
  { question: "Puff menthe ou fruits rouges ?", options: ["🌿 Menthe", "🍓 Fruits rouges", "🍇 Raisin", "❄️ Ice"] },
  { question: "Ton alcool préféré ?", options: ["🍺 Bière", "🍷 Vin", "🥃 Whisky", "🍹 Cocktail"] },
  { question: "Premier rendez-vous idéal ?", options: ["☕ Café", "🍽️ Restaurant", "🚶 Balade", "🎮 Gaming"] },
  { question: "Tu es plutôt...", options: ["🌅 Du matin", "🌙 Du soir", "😴 Toujours fatigué(e)"] },
  { question: "Weekend parfait ?", options: ["🏠 Cocooning", "🎊 Soirée", "🏃 Sport", "🎮 Jeux vidéo"] },
];

// ============================================
// 📸 Composant MessageBubble avec support photos
// ============================================
function MessageBubble({ msg, isDarkMode, customerId, onPhotoResponse, onReportPhoto, onDeleteMessage, onMessageRead, onReaction, onReply }) {
  const [photoData, setPhotoData] = useState(null);
  const [photoStatus, setPhotoStatus] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showFullPhoto, setShowFullPhoto] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [viewCount, setViewCount] = useState(0);
  const [maxViews, setMaxViews] = useState(1);
  const [photoExpired, setPhotoExpired] = useState(false);
  const menuRef = React.useRef(null);
  const reactionsRef = React.useRef(null);
  
  // Réactions disponibles
  const REACTIONS = ["❤️", "😂", "👍", "😮", "😢", "🔥"];
  
  // Charger la photo si c'est un message photo
  useEffect(() => {
    const loadPhoto = async () => {
      if (msg.type === 'photo_request' || msg.type === 'photo_accepted') {
        try {
          const photo = await api.getHeartbeatsPhoto(customerId, msg.photoId);
          setPhotoData(photo.photoData);
          setPhotoStatus(photo.status);
          setTimeRemaining(photo.timeRemaining);
          setViewCount(photo.viewCount || 0);
          setMaxViews(photo.maxViews || 1);
          // Vérifier si expirée par vues
          if (photo.status === 'viewed_limit_reached') {
            setPhotoExpired(true);
          }
        } catch (error) {
          console.error('Erreur chargement photo:', error);
        }
      }
    };
    if (msg.photoId) loadPhoto();
  }, [msg, customerId]);
  
  // Timer pour le temps restant
  useEffect(() => {
    if (timeRemaining && timeRemaining > 0) {
      const interval = setInterval(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timeRemaining]);
  
  // Fermer le menu en cliquant ailleurs
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
      if (reactionsRef.current && !reactionsRef.current.contains(e.target)) {
        setShowReactions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Marquer comme lu si message éphémère
  useEffect(() => {
    if (msg.isEphemeral && !msg.fromMe && !msg.read && !msg.deleted && onMessageRead) {
      onMessageRead(msg.id);
    }
  }, [msg, onMessageRead]);
  
  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return 'Expirée';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}min` : `${m}min`;
  };

  // Gérer le visionnage de photo (incrémenter les vues)
  const handlePhotoView = async () => {
    if (photoExpired || !msg.photoId || msg.fromMe) return;
    
    try {
      const result = await api.recordHeartbeatsPhotoView(msg.photoId, customerId);
      setViewCount(result.viewCount);
      if (result.expired) {
        setPhotoExpired(true);
        toast.info('👁️ Photo vue pour la dernière fois');
      }
    } catch (error) {
      console.error('Erreur enregistrement vue:', error);
    }
  };

  // Bloquer le clic droit et le téléchargement
  const handleContextMenu = (e) => {
    e.preventDefault();
    toast.error('📵 Téléchargement désactivé pour protéger la vie privée');
    return false;
  };
  
  // Message supprimé
  if (msg.deleted) {
    return (
      <div className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[80%] px-4 py-2 rounded-2xl italic ${
          isDarkMode ? 'bg-gray-800/50 text-gray-500' : 'bg-gray-100 text-gray-400'
        }`}>
          🗑️ Message supprimé
        </div>
      </div>
    );
  }
  
  // Message vocal
  if (msg.type === 'voice') {
    return (
      <VoiceMessageBubble 
        msg={msg} 
        isDarkMode={isDarkMode} 
        customerId={customerId}
        onReaction={onReaction}
      />
    );
  }
  
  // Message système
  if (msg.fromCustomerId === 'system' || msg.type?.includes('photo_')) {
    return (
      <div className="flex justify-center">
        <div className={`text-xs px-3 py-1 rounded-full ${isDarkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
          {msg.content || msg.text}
        </div>
      </div>
    );
  }
  
  // Message de demande de photo (pour le destinataire)
  if (msg.type === 'photo_request' && !msg.fromMe && photoStatus === 'pending') {
    return (
      <div className="flex justify-start">
        <div className={`max-w-[85%] p-4 rounded-2xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div className="flex items-center gap-2 mb-3">
            <Camera className="w-5 h-5 text-pink-500" />
            <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
              Veut partager une photo 📷
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => onPhotoResponse?.(msg.photoId, true)}
              className="bg-green-500 hover:bg-green-600 text-white"
            >
              <Eye className="w-4 h-4 mr-1" /> Voir
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onPhotoResponse?.(msg.photoId, false)}
              className={isDarkMode ? 'border-gray-600 text-gray-300' : ''}
            >
              <X className="w-4 h-4 mr-1" /> Refuser
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Photo expirée par limite de vues
  if (msg.type === 'photo_request' && (photoStatus === 'viewed_limit_reached' || photoExpired)) {
    return (
      <div className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${
          isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'
        }`}>
          <div className="flex items-center gap-2">
            <EyeOff className="w-5 h-5 text-gray-400" />
            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
              👁️ Photo vue ({maxViews} vue{maxViews > 1 ? 's' : ''} max)
            </span>
          </div>
        </div>
      </div>
    );
  }
  
  // Photo acceptée (visible) - AVEC PROTECTION ANTI-TÉLÉCHARGEMENT
  if (msg.type === 'photo_request' && photoStatus === 'accepted' && photoData) {
    return (
      <div className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[80%] rounded-2xl overflow-hidden ${
          msg.fromMe ? 'bg-gradient-to-r from-purple-500 to-pink-500' : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
        }`}>
          <div 
            className="relative select-none" 
            onContextMenu={handleContextMenu}
            style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
          >
            {/* Image avec protection anti-téléchargement */}
            <img 
              src={photoData} 
              alt="Photo partagée" 
              className="max-w-full h-auto cursor-pointer"
              onClick={() => {
                handlePhotoView();
                setShowFullPhoto(true);
              }}
              style={{ 
                maxHeight: '200px', 
                objectFit: 'cover',
                pointerEvents: 'auto',
                WebkitTouchCallout: 'none'
              }}
              draggable="false"
              onDragStart={(e) => e.preventDefault()}
            />
            {/* Overlay transparent pour bloquer les clics directs */}
            <div 
              className="absolute inset-0 bg-transparent" 
              onContextMenu={handleContextMenu}
              style={{ pointerEvents: 'none' }}
            />
            {/* Timer overlay */}
            <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTime(timeRemaining)}
            </div>
            {/* Compteur de vues */}
            {!msg.fromMe && (
              <div className="absolute bottom-2 right-12 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {viewCount}/{maxViews}
              </div>
            )}
            {/* Bouton signaler (pour le destinataire) */}
            {!msg.fromMe && (
              <button 
                onClick={() => onReportPhoto?.(msg.photoId)}
                className="absolute top-2 right-2 bg-black/60 text-white p-1.5 rounded-full hover:bg-red-500 transition-colors"
                title="Signaler"
              >
                <Flag className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
        
        {/* Modal photo plein écran - AVEC PROTECTION */}
        {showFullPhoto && (
          <Dialog open={showFullPhoto} onOpenChange={setShowFullPhoto}>
            <DialogContent 
              className="max-w-3xl bg-black/95"
              onContextMenu={handleContextMenu}
            >
              <div 
                className="select-none" 
                style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
                onContextMenu={handleContextMenu}
              >
                <img 
                  src={photoData} 
                  alt="Photo" 
                  className="w-full h-auto"
                  draggable="false"
                  onDragStart={(e) => e.preventDefault()}
                  style={{ WebkitTouchCallout: 'none' }}
                />
              </div>
              <div className="text-center text-white text-sm mt-2 flex items-center justify-center gap-4">
                <span>⏱️ Expire dans {formatTime(timeRemaining)}</span>
                {!msg.fromMe && <span>👁️ Vue {viewCount}/{maxViews}</span>}
              </div>
              <p className="text-center text-gray-500 text-xs">
                📵 Capture d'écran et téléchargement désactivés
              </p>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }
  
  // Photo envoyée en attente (pour l'envoyeur)
  if (msg.type === 'photo_request' && msg.fromMe && photoStatus === 'pending') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] px-4 py-3 rounded-2xl bg-gradient-to-r from-purple-500/50 to-pink-500/50 text-white">
          <div className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            <span>📷 Photo envoyée</span>
          </div>
          <p className="text-xs mt-1 opacity-80">⏳ En attente de validation...</p>
        </div>
      </div>
    );
  }
  
  // Message texte normal - AVEC MENU DE SUPPRESSION, RÉACTIONS ET RÉPONSE
  const reactions = msg.reactions || {};
  const myReaction = reactions[customerId];
  const replyTo = msg.replyTo;
  
  return (
    <div className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'} group relative mb-1`}>
      {/* Menu pour mes messages (à gauche) */}
      {msg.fromMe && (
        <div className="relative mr-2 self-center" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${
              isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/>
            </svg>
          </button>
          
          {/* Dropdown menu */}
          {showMenu && (
            <div className={`absolute right-0 bottom-full mb-1 rounded-lg shadow-lg py-1 min-w-[120px] z-50 ${
              isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
              <button
                onClick={() => {
                  onReply?.(msg);
                  setShowMenu(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                  isDarkMode ? 'hover:bg-gray-700 text-blue-400' : 'hover:bg-gray-100 text-blue-500'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
                </svg>
                Répondre
              </button>
              <button
                onClick={() => {
                  onDeleteMessage?.(msg.id);
                  setShowMenu(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                  isDarkMode ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-gray-100 text-red-500'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                </svg>
                Supprimer
              </button>
            </div>
          )}
        </div>
      )}
      
      <div className="relative max-w-[80%]">
        {/* Citation du message répondu */}
        {replyTo && (
          <div 
            className={`mb-1 px-3 py-1.5 rounded-lg text-xs border-l-2 ${
              msg.fromMe 
                ? 'bg-white/10 border-white/50 text-white/80' 
                : isDarkMode 
                  ? 'bg-gray-600/50 border-gray-400 text-gray-300' 
                  : 'bg-gray-300/50 border-gray-400 text-gray-600'
            }`}
          >
            <div className="font-semibold text-[10px] mb-0.5">
              ↩️ {replyTo.fromCustomerId === customerId ? 'Toi' : replyTo.authorName}
            </div>
            <div className="truncate">{replyTo.text}</div>
          </div>
        )}
        
        {/* Bulle de message */}
        <div
          className={`px-4 py-2 rounded-2xl relative ${
            msg.fromMe
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
              : isDarkMode
                ? 'bg-gray-700 text-white'
                : 'bg-gray-200 text-gray-900'
          }`}
          onDoubleClick={() => setShowReactions(true)}
        >
          {/* Indicateur message éphémère */}
          {msg.isEphemeral && (
            <div className="absolute -top-2 -right-2">
              <span className="text-xs bg-purple-500 text-white px-1.5 py-0.5 rounded-full">👻</span>
            </div>
          )}
          <LinkifyText text={msg.text || msg.content || ''} isDarkMode={isDarkMode} />
        </div>
        
        {/* Affichage des réactions existantes */}
        {Object.keys(reactions).length > 0 && (
          <div className={`absolute -bottom-3 ${msg.fromMe ? 'right-2' : 'left-2'} flex gap-0.5`}>
            <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs ${
              isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200 shadow-sm'
            }`}>
              {/* Afficher les réactions uniques */}
              {[...new Set(Object.values(reactions))].map((reaction, idx) => (
                <span key={idx} className="text-sm">{reaction}</span>
              ))}
              {Object.keys(reactions).length > 1 && (
                <span className={`text-xs ml-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {Object.keys(reactions).length}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Boutons au survol (réagir + répondre) */}
        <div className={`absolute -bottom-1 ${msg.fromMe ? 'left-0 -translate-x-full' : 'right-0 translate-x-full'} 
          opacity-0 group-hover:opacity-100 transition-opacity flex gap-1`}>
          <button
            onClick={() => onReply?.(msg)}
            className={`p-1 rounded-full ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-100 shadow'}`}
            title="Répondre"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
              <polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
            </svg>
          </button>
          <button
            onClick={() => setShowReactions(true)}
            className={`p-1 rounded-full ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-100 shadow'}`}
            title="Réagir"
          >
            <span className="text-xs">😀</span>
          </button>
        </div>
        
        {/* Sélecteur de réactions */}
        {showReactions && (
          <div 
            ref={reactionsRef}
            className={`absolute ${msg.fromMe ? 'right-0' : 'left-0'} bottom-full mb-2 
              flex gap-1 p-2 rounded-full shadow-lg z-50 ${
              isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
            }`}
          >
            {REACTIONS.map((reaction) => (
              <button
                key={reaction}
                onClick={() => {
                  onReaction?.(msg.id, myReaction === reaction ? null : reaction);
                  setShowReactions(false);
                }}
                className={`text-lg hover:scale-125 transition-transform p-1 rounded ${
                  myReaction === reaction 
                    ? isDarkMode ? 'bg-gray-600' : 'bg-gray-200' 
                    : ''
                }`}
              >
                {reaction}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Menu pour les messages reçus (à droite) */}
      {!msg.fromMe && (
        <div className="relative ml-2 self-center">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${
              isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/>
            </svg>
          </button>
          
          {/* Dropdown menu pour messages reçus */}
          {showMenu && (
            <div className={`absolute left-0 bottom-full mb-1 rounded-lg shadow-lg py-1 min-w-[120px] z-50 ${
              isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
              <button
                onClick={() => {
                  onReply?.(msg);
                  setShowMenu(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                  isDarkMode ? 'hover:bg-gray-700 text-blue-400' : 'hover:bg-gray-100 text-blue-500'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
                </svg>
                Répondre
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// 🎤 Composant Message Vocal
// ============================================
function VoiceMessageBubble({ msg, isDarkMode, customerId, onReaction }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration] = useState(msg.duration || 0);
  const audioRef = useRef(null);
  
  const togglePlay = async () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(msg.audioData);
      audioRef.current.onended = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };
      audioRef.current.ontimeupdate = () => {
        setCurrentTime(Math.floor(audioRef.current.currentTime));
      };
    }
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        // Marquer comme écouté
        if (!msg.fromMe && !msg.played) {
          api.markVoicePlayed(msg.id, customerId).catch(console.error);
        }
      } catch (error) {
        console.error('Erreur lecture audio:', error);
        toast.error('Impossible de lire le message vocal');
      }
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  
  return (
    <div className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'} group`}>
      <div
        className={`max-w-[70%] px-4 py-3 rounded-2xl flex items-center gap-3 ${
          msg.fromMe
            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
            : isDarkMode
              ? 'bg-gray-700 text-white'
              : 'bg-gray-200 text-gray-900'
        }`}
      >
        {/* Bouton Play/Pause */}
        <button
          onClick={togglePlay}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
            msg.fromMe 
              ? 'bg-white/20 hover:bg-white/30' 
              : isDarkMode 
                ? 'bg-gray-600 hover:bg-gray-500' 
                : 'bg-gray-300 hover:bg-gray-400'
          }`}
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" />
          )}
        </button>
        
        {/* Barre de progression */}
        <div className="flex-1 min-w-[80px]">
          <div className={`h-1.5 rounded-full overflow-hidden ${
            msg.fromMe ? 'bg-white/30' : isDarkMode ? 'bg-gray-500' : 'bg-gray-300'
          }`}>
            <div 
              className={`h-full transition-all ${msg.fromMe ? 'bg-white' : 'bg-pink-500'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-xs opacity-70">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        
        {/* Indicateur écouté */}
        {!msg.fromMe && msg.played && (
          <Volume2 className="w-4 h-4 opacity-50" />
        )}
      </div>
    </div>
  );
}


// ============================================
// ✍️ Composant pour polling du typing status
// Note: Désactivé automatiquement si WebSocket connecté
// ============================================
function TypingStatusEffect({ showChat, customerId, onTypingChange, isWebSocketConnected }) {
  useEffect(() => {
    if (!showChat?.matchId || !customerId) {
      onTypingChange(null);
      return;
    }
    
    // Si WebSocket connecté, pas besoin de polling HTTP
    if (isWebSocketConnected) {
      return;
    }
    
    const checkTyping = async () => {
      try {
        const result = await api.getTypingStatus(showChat.matchId, customerId);
        onTypingChange(result);
      } catch (error) {
        // Silently ignore errors
      }
    };
    
    checkTyping();
    const interval = setInterval(checkTyping, 1500); // Check every 1.5s
    
    return () => clearInterval(interval);
  }, [showChat?.matchId, customerId, onTypingChange, isWebSocketConnected]);
  
  return null; // Ce composant ne rend rien, il gère juste l'effet
}

// ============================================
// 📸 Composant Galerie Photos de Profil
// ============================================
function ProfilePhotosGallery({ isDarkMode, customerId, photos, onPhotosChange, canEdit = true }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Seules les images sont acceptées');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5 Mo)');
      return;
    }
    
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        await api.uploadProfilePhoto(customerId, base64);
        toast.success('📸 Photo uploadée avec succès !');
        onPhotosChange?.();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };
  
  const handleDelete = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await api.deleteProfilePhoto(customerId, photoId);
      toast.success('Photo supprimée');
      onPhotosChange?.();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  const handleSetMain = async (photoId) => {
    try {
      await api.setMainPhoto(customerId, photoId);
      toast.success('Photo principale mise à jour');
      onPhotosChange?.();
    } catch (error) {
      toast.error('Erreur');
    }
  };
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          📸 Mes photos ({photos?.length || 0}/5)
        </h4>
        {canEdit && photos?.length < 5 && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="text-sm px-3 py-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white"
          >
            {uploading ? '⏳' : '+ Ajouter'}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
      
      {photos?.length === 0 ? (
        <div className={`text-center py-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Ajoute des photos pour booster ton profil !
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo) => (
            <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden group">
              <img
                src={photo.data}
                alt="Photo profil"
                className="w-full h-full object-cover"
              />
              {photo.status === 'pending' && (
                <div className="absolute inset-0 bg-yellow-500/50 flex items-center justify-center">
                  <span className="text-xs font-bold text-white">⏳ En attente</span>
                </div>
              )}
              {photo.isMain && (
                <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  ⭐ Principale
                </div>
              )}
              {canEdit && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {!photo.isMain && photo.status === 'approved' && (
                    <button
                      onClick={() => handleSetMain(photo.id)}
                      className="p-1.5 rounded-full bg-white/20 hover:bg-white/40"
                      title="Définir comme principale"
                    >
                      ⭐
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(photo.id)}
                    className="p-1.5 rounded-full bg-red-500/80 hover:bg-red-500"
                    title="Supprimer"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// ✅ Composant Vérification de Profil (Style Tinder)
// ============================================
function VerificationModal({ isOpen, onClose, customerId, isDarkMode }) {
  const [step, setStep] = useState('intro'); // intro, pose, camera, submitting, done
  const [verificationId, setVerificationId] = useState(null);
  const [pose, setPose] = useState(null);
  const [selfieData, setSelfieData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (isOpen && customerId) {
      checkStatus();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, customerId]);

  const checkStatus = async () => {
    try {
      const res = await api.getVerificationStatus(customerId);
      setStatus(res);
      if (res.isVerified) {
        setStep('verified');
      } else if (res.status === 'submitted') {
        setStep('pending');
      } else if (res.status === 'rejected') {
        setStep('rejected');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const startVerification = async () => {
    setLoading(true);
    try {
      const res = await api.startVerification(customerId);
      setVerificationId(res.verificationId);
      setPose(res.pose);
      setStep('pose');
    } catch (error) {
      toast.error('Erreur lors du démarrage');
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setStep('camera');
    } catch (error) {
      toast.error('Impossible d\'accéder à la caméra');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const takeSelfie = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    // Miroir horizontal pour selfie naturel
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    
    const data = canvas.toDataURL('image/jpeg', 0.8);
    setSelfieData(data);
    stopCamera();
    setStep('preview');
  };

  const submitSelfie = async () => {
    if (!selfieData || !verificationId) return;
    
    setLoading(true);
    try {
      await api.submitVerificationSelfie(verificationId, selfieData);
      toast.success('✅ Selfie envoyé ! Vérification sous 24h');
      setStep('pending');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const retakeSelfie = () => {
    setSelfieData(null);
    startCamera();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`sm:max-w-md ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white'}`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
            <Shield className="w-6 h-6 text-blue-500" />
            Vérification de profil
          </DialogTitle>
        </DialogHeader>

        {/* Étape: Intro */}
        {step === 'intro' && (
          <div className="text-center py-4 space-y-4">
            <div className="text-6xl">✅</div>
            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Deviens un profil vérifié !
            </h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Prouve que c'est bien toi en prenant un selfie avec une pose spécifique.
              Tu obtiendras le badge <span className="text-blue-500 font-bold">✓ Vérifié</span> !
            </p>
            
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-blue-50'}`}>
              <div className="flex items-center justify-center gap-4 text-sm">
                <div className="text-center">
                  <div className="text-2xl mb-1">🛡️</div>
                  <div className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Plus de confiance</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1">💝</div>
                  <div className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Plus de matchs</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1">👑</div>
                  <div className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>+50 couronnes</div>
                </div>
              </div>
            </div>
            
            <Button
              onClick={startVerification}
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white"
            >
              {loading ? (
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>
                  <Camera className="w-5 h-5 mr-2" />
                  Commencer la vérification
                </>
              )}
            </Button>
          </div>
        )}

        {/* Étape: Pose à reproduire */}
        {step === 'pose' && pose && (
          <div className="text-center py-4 space-y-4">
            <div className="text-7xl animate-bounce">{pose.emoji}</div>
            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {pose.name}
            </h3>
            <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {pose.instruction}
            </p>
            
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-500/30' : 'bg-yellow-50 border border-yellow-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                ⚠️ Assure-toi d'être dans un endroit bien éclairé et que ton visage soit visible !
              </p>
            </div>
            
            <Button
              onClick={startCamera}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white"
            >
              <Camera className="w-5 h-5 mr-2" />
              Ouvrir la caméra
            </Button>
          </div>
        )}

        {/* Étape: Caméra */}
        {step === 'camera' && (
          <div className="space-y-4">
            <div className="relative rounded-xl overflow-hidden bg-black">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full aspect-square object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
              
              {/* Overlay avec la pose */}
              <div className="absolute top-2 left-2 right-2 bg-black/60 rounded-lg p-2 text-center">
                <span className="text-2xl mr-2">{pose?.emoji}</span>
                <span className="text-white text-sm">{pose?.instruction}</span>
              </div>
            </div>
            
            <canvas ref={canvasRef} className="hidden" />
            
            <Button
              onClick={takeSelfie}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-6 text-lg"
            >
              📸 Prendre le selfie
            </Button>
          </div>
        )}

        {/* Étape: Prévisualisation */}
        {step === 'preview' && selfieData && (
          <div className="space-y-4">
            <div className="relative rounded-xl overflow-hidden">
              <img src={selfieData} alt="Selfie" className="w-full aspect-square object-cover" />
              
              <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-sm">
                {pose?.emoji} {pose?.name}
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={retakeSelfie}
                variant="outline"
                className="flex-1"
              >
                🔄 Reprendre
              </Button>
              <Button
                onClick={submitSelfie}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white"
              >
                {loading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  '✅ Envoyer'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Étape: En attente de modération */}
        {step === 'pending' && (
          <div className="text-center py-6 space-y-4">
            <div className="text-6xl">⏳</div>
            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Vérification en cours...
            </h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Notre équipe examine ton selfie. Tu recevras une notification sous 24h !
            </p>
            <Button onClick={onClose} variant="outline" className="mt-4">
              Fermer
            </Button>
          </div>
        )}

        {/* Étape: Déjà vérifié */}
        {step === 'verified' && (
          <div className="text-center py-6 space-y-4">
            <div className="text-6xl">✅</div>
            <h3 className={`text-xl font-bold text-green-500`}>
              Profil vérifié !
            </h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Ton badge <span className="text-blue-500 font-bold">✓ Vérifié</span> est affiché sur ton profil.
              Les autres utilisateurs peuvent te faire confiance !
            </p>
            <Button onClick={onClose} className="bg-green-500 text-white mt-4">
              Super ! 🎉
            </Button>
          </div>
        )}

        {/* Étape: Rejeté */}
        {step === 'rejected' && (
          <div className="text-center py-6 space-y-4">
            <div className="text-6xl">😕</div>
            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Vérification refusée
            </h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {status?.rejectReason || 'La pose ne correspondait pas ou le visage n\'était pas visible.'}
            </p>
            <Button
              onClick={startVerification}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white mt-4"
            >
              🔄 Réessayer
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// 🔧 UTILITAIRE: Récupération robuste du customerId
const getCustomerIdFromStorage = () => {
  // 1. Try heartbeats_user (standalone users)
  try {
    const savedUser = localStorage.getItem('heartbeats_user');
    if (savedUser) {
      const parsed = JSON.parse(savedUser);
      if (parsed?.id) return parsed.id;
    }
  } catch (e) {}
  
  // 2. Try lpep_customer (LPE users)
  try {
    const savedCustomer = localStorage.getItem('lpep_customer');
    if (savedCustomer) {
      const parsed = JSON.parse(savedCustomer);
      if (parsed?.id) return parsed.id;
    }
  } catch (e) {}
  
  return null;
};

// Composant principal Heartbeats
export default function HeartbeatsModule({ customer, isDarkMode, onClose }) {
  // 🔧 FALLBACK: customerId robuste (prop OU localStorage)
  const customerId = customer?.id || getCustomerIdFromStorage();
  
  // 🔍 DEBUG: Logger l'état initial
  console.log('🔍 HeartbeatsModule INIT:', { 
    customerProp: customer?.id, 
    customerId, 
    fromStorage: getCustomerIdFromStorage() 
  });
  
  // 🔥 ZUSTAND STORE - États centralisés
  const {
    profile, setProfile, fetchProfile,
    discoverProfiles, currentProfileIndex, fetchDiscoverProfiles, setDiscoverProfiles, nextProfile, removeCurrentProfile,
    matches, setMatches, fetchMatches,
    activeTab, setActiveTab,
    isAtStore, setIsAtStore,
    isVisible, setIsVisible,
    crowns, fetchCrowns,
    onlineUsers: storeOnlineUsers, setOnlineUsers,
    crushes, setCrushes, receivedCrushes, setReceivedCrushes,
    messages: storeMessages, setMessages: setStoreMessages, addMessage,
    addNotification
  } = useHeartbeatsStore();

  // États locaux (UI uniquement - modals, états temporaires)
  const [showMatch, setShowMatch] = useState(null);
  const [showChat, setShowChat] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [showPassionsModal, setShowPassionsModal] = useState(false);  // 🌌 Étape 2 : Passions Profondes
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [icebreaker, setIcebreaker] = useState(null);
  const [selectedIcebreaker, setSelectedIcebreaker] = useState(null);
  const [blindDateMode, setBlindDateMode] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);  // Message auquel on répond
  const [typingStatus, setTypingStatus] = useState(null);  // "Sophie est en train d'écrire..."
  const [activeVideoCall, setActiveVideoCall] = useState(null);  // Appel vidéo en cours
  const [incomingCall, setIncomingCall] = useState(null);  // Appel entrant
  const [showGiftModal, setShowGiftModal] = useState(null);  // Modal cadeaux { targetId, targetName }
  const [showVerificationModal, setShowVerificationModal] = useState(false);  // Modal vérification
  const [showSuperLikeModal, setShowSuperLikeModal] = useState(null);  // Modal Super Like { targetId, targetName, targetAvatar }
  const [showCrownShop, setShowCrownShop] = useState(false);  // Boutique couronnes
  const [showGiftShopModal, setShowGiftShopModal] = useState(null);  // Modal cadeaux boutique { recipientId, recipientName }
  const [showMyGifts, setShowMyGifts] = useState(false);  // Mes cadeaux reçus
  const [showPaywall, setShowPaywall] = useState(null);  // 💎 Paywall modal { feature: string }
  const [isPremium, setIsPremium] = useState(false);  // 💎 Statut Premium de l'utilisateur
  const typingTimeoutRef = useRef(null);
  
  // 💜 LUNA & ENZO - Les guides Heartbeats (Déclenchement intelligent)
  const [showGuide, setShowGuide] = useState(false);
  const [guideType, setGuideType] = useState('welcome');
  const [guideDismissed, setGuideDismissed] = useState(false);
  
  // 💎 HBT TOKEN SYSTEM
  const [hbtWallet, setHbtWallet] = useState(null);
  const [showHBTShop, setShowHBTShop] = useState(false);
  const [giftTargetProfile, setGiftTargetProfile] = useState(null);
  const [receivedGift, setReceivedGift] = useState(null);
  
  // 🗣️ SALONS THÉMATIQUES
  const [showThemedRooms, setShowThemedRooms] = useState(false);
  const [activeRoom, setActiveRoom] = useState(null);
  const [joinedRooms, setJoinedRooms] = useState([]);
  const [roomMessages, setRoomMessages] = useState({});
  
  // 💬 ACCROCHE MAGIQUE - La phrase qui définit ton mood !
  const [showTaglineEditor, setShowTaglineEditor] = useState(false);
  
  // Déterminer quel guide afficher (Luna ou Enzo) selon les préférences
  const guideCharacter = profile ? getGuideForUser(
    customer?.gender || 'male', 
    profile?.lookingFor || 'all'
  ) : 'luna';
  
  // 💜 DÉCLENCHEMENT INTELLIGENT DES GUIDES
  // 🔧 FIX: Utiliser customerId au lieu de customer?.id
  useEffect(() => {
    if (!customerId || guideDismissed) return;
    
    // Vérifier les conditions de déclenchement
    const welcomeSeen = localStorage.getItem(`heartbeats_guide_welcome_seen_${customerId}`);
    const firstMatchSeen = localStorage.getItem(`heartbeats_guide_firstMatch_seen_${customerId}`);
    const encouragementSeen = localStorage.getItem(`heartbeats_guide_encouragement_seen_${customerId}`);
    
    // 1. Premier accès : message de bienvenue
    if (!welcomeSeen && !showGuide) {
      setTimeout(() => {
        setGuideType('welcome');
        setShowGuide(true);
      }, 2000);
      return;
    }
    
    // 2. Premier match : célébration !
    if (!firstMatchSeen && matches.length > 0 && !showGuide) {
      setTimeout(() => {
        setGuideType('firstMatch');
        setShowGuide(true);
      }, 1500);
      localStorage.setItem(`heartbeats_guide_firstMatch_seen_${customerId}`, 'true');
      return;
    }
    
    // 3. Encouragement si pas de match après 10 swipes
    if (!encouragementSeen && !showGuide && currentProfileIndex > 10 && matches.length === 0) {
      setGuideType('encouragement');
      setShowGuide(true);
      localStorage.setItem(`heartbeats_guide_encouragement_seen_${customerId}`, 'true');
    }
  }, [customerId, matches.length, currentProfileIndex, guideDismissed, showGuide]);
  
  // 💎 Charger le wallet HBT
  // 🔧 FIX: Utiliser customerId au lieu de customer?.id
  useEffect(() => {
    const loadHBTWallet = async () => {
      if (!customerId) return;
      try {
        const res = await api.getHBTWallet(customerId);
        setHbtWallet(res.data);
      } catch (error) {
        console.error('Erreur chargement wallet HBT:', error);
      }
    };
    loadHBTWallet();
  }, [customerId]);
  
  // 🖤 SHADOW MODE
  const [showShadowMode, setShowShadowMode] = useState(false);
  
  // 👤 MON PROFIL - Vue dédiée
  const [showMyProfile, setShowMyProfile] = useState(false);
  const [profileStats, setProfileStats] = useState({ views: 0, likesReceived: 0, matches: 0, conversations: 0 });
  
  // 👻 MODE CONFIDENTIEL - Pour la discrétion absolue
  const [showConfidentialSettings, setShowConfidentialSettings] = useState(false);
  const [isConfidentialMode, setIsConfidentialMode] = useState(() => {
    return localStorage.getItem(`heartbeats_confidential_${customerId}`) === 'true';
  });
  const [confidentialPin, setConfidentialPin] = useState('');
  const [showPinEntry, setShowPinEntry] = useState(false);
  
  // 🗑️ SUPPRESSION DE COMPTE HEARTBEATS (RGPD)
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  
  // 👻 MODE INVISIBLE & PARAMÈTRES PREMIUM
  const [showPremiumSettings, setShowPremiumSettings] = useState(false);
  const [ghostMode, setGhostMode] = useState(false);
  const [boostActive, setBoostActive] = useState(false);
  const [boostEndTime, setBoostEndTime] = useState(null);
  
  // 📸 STORIES - Contenu éphémère 24h
  const [stories, setStories] = useState([]);
  const [showStoryCreator, setShowStoryCreator] = useState(false);
  const [showStoryViewer, setShowStoryViewer] = useState(null); // { stories, initialIndex }
  
  // 🔥 VIRAL FEATURES
  const [showHotOrNot, setShowHotOrNot] = useState(false);
  const [showSecretCrush, setShowSecretCrush] = useState(null); // profil cible
  const [showShareCard, setShowShareCard] = useState(false);
  const [showInviteFriends, setShowInviteFriends] = useState(false);
  const [showAffiliation, setShowAffiliation] = useState(false);  // 💰 Programme Affiliation MLM
  const [hotOrNotPair, setHotOrNotPair] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  
  // 🎮 GAMES - Mini-jeux Dating
  const [showGameLobby, setShowGameLobby] = useState(null); // { partnerId, partnerName }
  const [currentGame, setCurrentGame] = useState(null); // { type, partnerId, partnerName }
  
  // 🙈 BLIND DATE - Matcher sans voir les photos
  const [showBlindDate, setShowBlindDate] = useState(false);
  const [blindDateProfiles, setBlindDateProfiles] = useState([]);
  const [showRevealMoment, setShowRevealMoment] = useState(null); // profil à révéler
  
  // 🚀 PIXIE - La commandante de bord
  const [showIcebreakers, setShowIcebreakers] = useState(null); // profil cible
  const [pixieAdvice, setPixieAdvice] = useState(null); // conseil actuel
  
  // 🌟 SOUL & PSYCHE - Quiz et profils
  const [showSoulQuiz, setShowSoulQuiz] = useState(false);
  const [showPsycheQuiz, setShowPsycheQuiz] = useState(false);
  const [soulCompleted, setSoulCompleted] = useState(false);
  const [psycheCompleted, setPsycheCompleted] = useState(false);
  
  // ⚖️ DOCUMENTS LÉGAUX - CGU et Confidentialité Heartbeats
  const [showHeartbeatsCGU, setShowHeartbeatsCGU] = useState(false);
  const [showHeartbeatsPrivacy, setShowHeartbeatsPrivacy] = useState(false);
  
  // 🔐 COFFRE SPATIAL - Système de déverrouillage sécurisé
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [vaultData, setVaultData] = useState(null);
  const [showVault, setShowVault] = useState(true); // Afficher le coffre par défaut
  const [vaultLoading, setVaultLoading] = useState(true);
  
  // ⏱️ TIMEOUT DE SECOURS ABSOLU - Force l'arrêt du loading après 6 secondes
  useEffect(() => {
    const emergencyTimeout = setTimeout(() => {
      if (loading || vaultLoading) {
        console.log('⏱️ EMERGENCY TIMEOUT 6s - État actuel:', {
          loading,
          vaultLoading,
          vaultUnlocked,
          customerId,
          hasHeartbeatsToken: !!localStorage.getItem('heartbeats_token'),
          hasHeartbeatsUser: !!localStorage.getItem('heartbeats_user')
        });
        console.log('⏱️ Forçage arrêt loading pour débloquer l\'interface');
        setLoading(false);
        setVaultLoading(false);
        setVaultUnlocked(true); // Débloquer le vault aussi
      }
    }, 6000);
    
    return () => clearTimeout(emergencyTimeout);
  }, [loading, vaultLoading, vaultUnlocked, customerId]); // Dépendances pour le logging
  
  // 🔐 Vérifier si le coffre est déjà déverrouillé
  // ✅ NOUVEAU: Les utilisateurs standalone (heartbeats.fr) n'ont PAS besoin du coffre
  useEffect(() => {
    const checkVaultStatus = async () => {
      console.log('🔐 checkVaultStatus démarré:', {
        hasToken: !!localStorage.getItem('heartbeats_token'),
        hasUser: !!localStorage.getItem('heartbeats_user'),
        customerId,
        customerPropId: customer?.id
      });
      
      // ✅ PRIORITÉ 1: Vérifier si c'est un utilisateur standalone (heartbeats.fr)
      // Ces utilisateurs ont déjà vérifié leur email et leur âge à l'inscription
      const heartbeatsToken = localStorage.getItem('heartbeats_token');
      const heartbeatsUser = localStorage.getItem('heartbeats_user');
      const heartbeatsAgeVerified = localStorage.getItem('heartbeats_age_verified');
      
      // Si l'utilisateur vient de heartbeats.fr (a un token), bypass le coffre immédiatement
      if (heartbeatsToken && heartbeatsUser) {
        console.log('✅ Utilisateur standalone heartbeats.fr - Coffre bypassed');
        try {
          const userData = JSON.parse(heartbeatsUser);
          const simulatedVaultData = {
            unlocked: true,
            source: 'heartbeats.fr',
            email: userData.email,
            emailVerified: true,
            isAdult: true,
            birthDate: heartbeatsAgeVerified ? JSON.parse(heartbeatsAgeVerified).birthDate : null,
            unlockedAt: new Date().toISOString()
          };
          setVaultData(simulatedVaultData);
          setVaultUnlocked(true);
          setShowVault(false);
          setVaultLoading(false);
          return;
        } catch (e) {
          console.error('Erreur parsing heartbeats user:', e);
          // 🔧 FIX CRITIQUE: Même en cas d'erreur de parsing, débloquer le vault
          // pour éviter le blocage infini de l'écran de chargement
          console.log('⚠️ Erreur parsing mais token présent - Déverrouillage forcé');
          setVaultUnlocked(true);
          setVaultLoading(false);
          return;
        }
      }
      
      // ✅ PRIORITÉ 2: Si pas de customer.id (et pas de token heartbeats), on ne peut rien faire
      // On déverrouille quand même pour éviter le blocage
      if (!customer?.id && !customerId) {
        console.log('⚠️ Pas de customer.id ni customerId - Bypass vault pour éviter blocage');
        setVaultUnlocked(true);
        setVaultLoading(false);
        return;
      }
      
      const effectiveCustomerId = customer?.id || customerId;
      
      // Pour les utilisateurs LPE, vérifier le coffre comme avant
      // Vérifier d'abord dans le localStorage
      const storedVault = localStorage.getItem(`heartbeats_vault_${effectiveCustomerId}`);
      if (storedVault) {
        try {
          const parsed = JSON.parse(storedVault);
          setVaultData(parsed);
          setVaultUnlocked(true);
          setShowVault(false);
          setVaultLoading(false);
          return;
        } catch (e) {
          console.error('Erreur parsing vault data:', e);
          // 🔧 FIX: Supprimer les données corrompues du localStorage
          localStorage.removeItem(`heartbeats_vault_${effectiveCustomerId}`);
        }
      }
      
      // Sinon vérifier côté serveur
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/heartbeats/vault-status/${effectiveCustomerId}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          if (data.unlocked) {
            setVaultData(data);
            setVaultUnlocked(true);
            setShowVault(false);
          }
        }
      } catch (error) {
        console.error('Erreur vérification vault:', error);
        // En cas d'erreur API ou timeout, on déverrouille pour éviter le blocage
        setVaultUnlocked(true);
      } finally {
        // 🔧 FIX CRITIQUE: TOUJOURS arrêter vaultLoading dans finally
        setVaultLoading(false);
      }
    };
    
    checkVaultStatus();
  }, [customer?.id, customerId]);
  
  // 🔓 Handler pour déverrouiller le coffre
  const handleVaultUnlock = async (data) => {
    setVaultData(data);
    setVaultUnlocked(true);
    setShowVault(false);
    
    // Sauvegarder côté serveur
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/heartbeats/unlock-vault`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: customer?.id,
          birthDate: data.birthDate,
          pin: data.pin,
          email: data.email,
          ghostMode: data.ghostMode
        })
      });
    } catch (error) {
      console.error('Erreur sauvegarde vault:', error);
    }
  };
  
  // Charger les stats du profil
  // 🔧 FIX: Utiliser customerId au lieu de customer?.id
  useEffect(() => {
    const loadProfileStats = async () => {
      if (!customerId) return;
      // Simuler les stats pour l'instant (à connecter au backend plus tard)
      setProfileStats({
        views: Math.floor(Math.random() * 50) + 10,
        likesReceived: receivedCrushes?.length || 0,
        matches: matches?.length || 0,
        conversations: Object.keys(storeMessages || {}).length
      });
    };
    loadProfileStats();
  }, [customerId, receivedCrushes?.length, matches?.length, storeMessages]);

  // 💎 Vérifier le statut Premium de l'utilisateur
  useEffect(() => {
    const checkPremiumStatus = async () => {
      // On utilise profile.id (ObjectId du profil Heartbeats) et non customer.id
      const profileId = profile?.id || profile?._id;
      if (!profileId) return;
      
      try {
        // ✅ Appel vers /api/subscription/status/{profile_id} (endpoint correct)
        const res = await fetch(`${API_URL}/api/subscription/status/${profileId}`);
        if (res.ok) {
          const data = await res.json();
          // Le backend retourne { subscription: { tier, is_active, ... } }
          const sub = data.subscription || {};
          const isPremiumUser = sub.tier === 'premium' || sub.tier === 'vip' || sub.is_active;
          setIsPremium(isPremiumUser);
          console.log('💎 Statut Premium:', isPremiumUser, 'Tier:', sub.tier);
        }
      } catch (error) {
        console.log('💎 Vérification premium échouée, mode gratuit');
        setIsPremium(false);
      }
    };
    
    // Vérifier aussi via le profil local (si déjà marqué premium)
    if (profile?.isPremium || profile?.subscription?.tier === 'premium' || profile?.subscription?.tier === 'vip') {
      setIsPremium(true);
    } else {
      checkPremiumStatus();
    }
  }, [profile?.id, profile?._id, profile?.isPremium, profile?.subscription?.tier]);

  // Alias pour compatibilité avec le code existant
  const profiles = discoverProfiles;
  const currentIndex = currentProfileIndex;
  const atStore = isAtStore;
  const setAtStore = setIsAtStore;
  const setProfiles = setDiscoverProfiles;  // Alias pour setProfiles
  
  // Messages du chat actif (depuis le store)
  const messages = showChat?.matchId ? (storeMessages[showChat.matchId] || []) : [];
  const setMessages = (msgs) => {
    if (showChat?.matchId) {
      if (typeof msgs === 'function') {
        // Gérer le cas où msgs est une fonction (prev => [...prev, newMsg])
        const currentMsgs = storeMessages[showChat.matchId] || [];
        setStoreMessages(showChat.matchId, msgs(currentMsgs));
      } else {
        setStoreMessages(showChat.matchId, msgs);
      }
    }
  };

  // 🔌 WebSocket - Remplace le polling par du temps réel
  const { 
    isConnected, 
    onlineUsers, 
    typingUsers,
    joinMatch, 
    leaveMatch, 
    startTyping: socketStartTyping, 
    stopTyping: socketStopTyping,
    checkinStore,
    checkoutStore,
    on: socketOn,
    off: socketOff
  } = useSocket(customerId);  // 🔧 FIX: Utiliser customerId (hb_*) au lieu de customer?.id

  // Ref pour stocker loadMatches (évite la dépendance circulaire)
  const loadMatchesRef = useRef(null);

  // Rejoindre/quitter la room du chat quand on ouvre/ferme
  useEffect(() => {
    if (showChat?.matchId && isConnected) {
      joinMatch(showChat.matchId);
      return () => leaveMatch(showChat.matchId);
    }
  }, [showChat?.matchId, isConnected, joinMatch, leaveMatch]);

  // Utiliser le typing WebSocket au lieu du polling
  useEffect(() => {
    if (showChat?.matchId && typingUsers[showChat.matchId]) {
      const typingUserIds = Object.keys(typingUsers[showChat.matchId] || {})
        .filter(id => id !== customerId);  // 🔧 FIX: Utiliser customerId
      if (typingUserIds.length > 0) {
        setTypingStatus(`✍️ En train d'écrire...`);
      } else {
        setTypingStatus(null);
      }
    }
  }, [showChat?.matchId, typingUsers, customerId]);

  // Heartbeat de présence - via WebSocket si connecté, sinon fallback HTTP
  // 🔧 FIX: Utiliser customerId (hb_*) au lieu de customer.id (peut être cust_*)
  useEffect(() => {
    const effectiveId = profile?.customerId || customerId;
    if (!effectiveId || !profile) return;
    
    // Si WebSocket connecté, pas besoin de polling HTTP
    if (isConnected) return;
    
    const sendHeartbeat = () => {
      api.updateHeartbeatsPresence(effectiveId).catch(console.error);
    };
    
    sendHeartbeat(); // Initial
    const interval = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(interval);
  }, [customerId, profile, isConnected]);

  // Polling des appels entrants - désactivé si WebSocket connecté
  // 🔧 FIX: Utiliser customerId (hb_*) au lieu de customer.id (peut être cust_*)
  useEffect(() => {
    // Utiliser le customerId du profil heartbeats, pas l'ID du customer LPE
    const effectiveId = profile?.customerId || customerId;
    if (!effectiveId || !profile) return;
    
    // Si WebSocket connecté, on utilise les événements temps réel
    if (isConnected) return;
    
    const checkIncoming = async () => {
      try {
        const result = await api.checkIncomingCall(effectiveId);
        if (result.hasIncomingCall && !incomingCall && !activeVideoCall) {
          setIncomingCall(result);
        }
      } catch (error) {}
    };
    
    checkIncoming();
    const interval = setInterval(checkIncoming, 3000);
    return () => clearInterval(interval);
  }, [customerId, profile, incomingCall, activeVideoCall, isConnected]);

  // Vérifier si le client est majeur
  // NOTE: Cette vérification est maintenant gérée par le Coffre Spatial Heartbeats
  // Si le vault est déverrouillé, on sait que l'âge a été vérifié
  const isAdult = useCallback(() => {
    // Si le coffre est déverrouillé, l'âge a été vérifié
    if (vaultUnlocked) return true;
    
    // Si pas de date de naissance renseignée, on laisse le coffre vérifier
    if (!customer?.birthDate) return true;
    
    const birth = new Date(customer.birthDate);
    const today = new Date();
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return age - 1 >= 18;
    }
    return age >= 18;
  }, [customer, vaultUnlocked]);

  // Charger le profil Heartbeats
  const loadProfile = useCallback(async () => {
    if (!customerId || !isAdult()) return;
    try {
      const data = await api.getHeartbeatsProfile(customerId);
      setProfile(data);
      setIsVisible(data?.isVisible || false);
      
      // 💎 Mettre à jour le statut Premium depuis le profil
      // Les clients LPE ont isPremium=true automatiquement
      if (data?.isPremium) {
        setIsPremium(true);
        console.log('💎 Statut Premium (depuis profil):', true, 'Tier:', data?.tier);
      }
      
      // Ne montrer le setup QUE si le profil n'existe vraiment pas (pas de customerId)
      if (!data || !data.customerId) {
        setShowSetup(true);
      }
    } catch (error) {
      console.log('Pas de profil Heartbeats, création...');
      setShowSetup(true);
    }
  }, [customerId, isAdult]);

  // Charger les profils disponibles
  const loadProfiles = useCallback(async () => {
    if (!customerId || !isAdult()) return;
    try {
      const data = await api.getHeartbeatsProfiles(customerId);
      // L'API retourne { profiles: [] }, on extrait le tableau
      setProfiles(data?.profiles || data || []);
    } catch (error) {
      console.error('Erreur chargement profils:', error);
      setProfiles([]);
    }
  }, [customerId, isAdult]);

  // Charger les matchs
  const loadMatches = useCallback(async () => {
    if (!customerId) return;
    try {
      const data = await api.getHeartbeatsMatches(customerId);
      // L'API retourne { matches: [] }, on extrait le tableau
      setMatches(data?.matches || data || []);
    } catch (error) {
      console.error('Erreur chargement matchs:', error);
      setMatches([]); // Fallback à un tableau vide en cas d'erreur
    }
  }, [customerId]);

  // Charger les crushes envoyés et reçus
  const loadCrushes = useCallback(async () => {
    if (!customerId) return;
    try {
      const [sent, received] = await Promise.all([
        api.getHeartbeatsCrushes(customerId),
        api.getHeartbeatsReceivedCrushes(customerId)
      ]);
      // L'API retourne { crushes: [] }, on extrait le tableau
      setCrushes(sent?.crushes || sent || []);
      setReceivedCrushes(received?.crushes || received || []);
    } catch (error) {
      console.error('Erreur chargement crushes:', error);
      setCrushes([]);
      setReceivedCrushes([]);
    }
  }, [customerId]);

  // 📸 Charger les Stories du quartier
  const loadStories = useCallback(async () => {
    if (!customerId) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      // Essayer d'obtenir la position
      let lat = null, lng = null;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        } catch (e) {
          console.log('Géolocalisation non disponible pour les stories');
        }
      }
      
      const url = lat && lng 
        ? `${API_URL}/api/stories/feed/${customerId}?latitude=${lat}&longitude=${lng}`
        : `${API_URL}/api/stories/feed/${customerId}`;
      
      const response = await axios.get(url);
      setStories(response.data.stories || []);
    } catch (error) {
      console.error('Erreur chargement stories:', error);
      setStories([]);
    }
  }, [customerId]);

  // 🔥 Charger les infos de parrainage
  const loadReferralInfo = useCallback(async () => {
    if (!customerId) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${API_URL}/api/viral/referral/${customerId}`);
      setReferralInfo(response.data);
    } catch (error) {
      console.error('Erreur chargement referral:', error);
    }
  }, [customerId]);

  // 🔥 Publier une Story
  const publishStory = useCallback(async (storyData) => {
    if (!customerId) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${API_URL}/api/stories/${customerId}`, storyData);
      // Recharger les stories
      loadStories();
      return response.data;
    } catch (error) {
      console.error('Erreur publication story:', error);
      toast.error('Erreur lors de la publication');
    }
  }, [customerId, loadStories]);

  // 🔥 Voter Hot or Not
  const voteHotOrNot = useCallback(async (winnerId) => {
    if (!customerId || !hotOrNotPair) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const loserId = hotOrNotPair.find(p => p.id !== winnerId)?.id;
      await axios.post(`${API_URL}/api/viral/hot-or-not/vote?customer_id=${customerId}`, {
        winnerId,
        loserId
      });
      // Charger une nouvelle paire
      const response = await axios.get(`${API_URL}/api/viral/hot-or-not/pair/${customerId}`);
      setHotOrNotPair(response.data.pair);
      toast.success('🔥 Vote enregistré !');
    } catch (error) {
      console.error('Erreur vote:', error);
    }
  }, [customerId, hotOrNotPair]);

  // 💘 Envoyer un crush secret
  const sendSecretCrush = useCallback(async (crushData) => {
    if (!customerId) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${API_URL}/api/viral/secret-crush?customer_id=${customerId}`, crushData);
      
      if (response.data.isMatch) {
        // 🎉 MATCH !
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#EC4899', '#8B5CF6', '#3B82F6']
        });
        toast.success('💘 C\'est réciproque ! Vous avez matché !');
        loadMatches(); // Recharger les matchs
      } else {
        toast.success('💘 Crush envoyé ! On croise les doigts...');
      }
      
      setShowSecretCrush(null);
      return response.data;
    } catch (error) {
      console.error('Erreur crush secret:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'envoi');
    }
  }, [customer, loadMatches]);

  // 🙈 Charger les profils Blind Date
  // 🔧 FIX: Utiliser customerId au lieu de customer?.id
  const loadBlindDateProfiles = useCallback(async () => {
    if (!customerId) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${API_URL}/api/blind-date/profiles/${customerId}`);
      setBlindDateProfiles(response.data.profiles || []);
    } catch (error) {
      console.error('Erreur chargement blind date:', error);
    }
  }, [customerId]);

  // 🎮 Démarrer un mini-jeu
  // 🔧 FIX: Utiliser customerId au lieu de customer?.id
  const startGame = useCallback(async (gameType, partnerId, partnerName) => {
    if (!customerId) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${API_URL}/api/games/session/start?customer_id=${customerId}`, {
        targetId: partnerId,
        gameType: gameType
      });
      setCurrentGame({ type: gameType, partnerId, partnerName, session: response.data.session });
      setShowGameLobby(null);
      toast.success(`🎮 Partie ${gameType} lancée !`);
    } catch (error) {
      console.error('Erreur démarrage jeu:', error);
      // Jouer quand même en local
      setCurrentGame({ type: gameType, partnerId, partnerName });
      setShowGameLobby(null);
    }
  }, [customerId]);

  // 🧚‍♀️ Obtenir des icebreakers de Pixie
  // 🔧 FIX: Utiliser customerId au lieu de customer?.id
  const getIcebreakers = useCallback(async (targetProfileId, category = 'passion') => {
    if (!customerId) return [];
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${API_URL}/api/pixie/icebreakers?customer_id=${customerId}`, {
        targetProfileId,
        category,
        count: 3
      });
      return response.data.icebreakers || [];
    } catch (error) {
      console.error('Erreur icebreakers:', error);
      return [];
    }
  }, [customerId]);

  // 🧚‍♀️ Demander un conseil à Pixie
  // 🔧 FIX: Utiliser customerId au lieu de customer?.id
  const getPixieAdvice = useCallback(async (context, targetName = null) => {
    if (!customerId) return null;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${API_URL}/api/pixie/advice?customer_id=${customerId}`, {
        context,
        targetName
      });
      setPixieAdvice(response.data.advice);
      return response.data.advice;
    } catch (error) {
      console.error('Erreur pixie advice:', error);
      return null;
    }
  }, [customerId]);

  useEffect(() => {
    // ⚠️ ATTENDRE que le vault soit vérifié avant de charger les données
    if (vaultLoading) {
      console.log('🔍 Attente vérification vault...');
      return;
    }
    
    const init = async () => {
      console.log('🔍 HeartbeatsModule INIT useEffect:', { customerId, vaultUnlocked, isAdultResult: isAdult() });
      setLoading(true);
      try {
        // Charger les données avec timeout de sécurité
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout chargement')), 10000)
        );
        
        const loadDataPromise = (async () => {
          console.log('🔍 Loading profile...');
          await loadProfile();
          console.log('🔍 Loading profiles...');
          await loadProfiles();
          console.log('🔍 Loading matches...');
          await loadMatches();
          console.log('🔍 Loading crushes...');
          await loadCrushes();
          console.log('🔍 Loading stories & referral (parallel)...');
          // 📸 Charger les stories et infos virales en parallèle (non-blocking)
          loadStories();
          loadReferralInfo();
        })();
        
        await Promise.race([loadDataPromise, timeoutPromise]);
        console.log('🔍 All data loaded successfully');
      } catch (error) {
        console.error('❌ Erreur chargement HeartbeatsModule:', error);
        // Continuer quand même pour afficher l'interface
      } finally {
        // TOUJOURS arrêter le loading, même en cas d'erreur
        console.log('🔍 Setting loading to false');
        setLoading(false);
      }
    };
    
    if (customerId && isAdult()) {
      init();
    } else {
      console.log('🔍 Skipping init:', { customerId, isAdultResult: isAdult(), vaultUnlocked });
      setLoading(false);
    }
  }, [customerId, vaultLoading, vaultUnlocked, loadProfile, loadProfiles, loadMatches, loadCrushes, loadStories, loadReferralInfo, isAdult]);

  // 💜 Afficher le guide de bienvenue (Luna/Enzo) au premier accès
  useEffect(() => {
    if (profile && !loading) {
      // Vérifier si le guide de bienvenue a déjà été vu
      const welcomeSeen = localStorage.getItem('heartbeats_guide_welcome_seen');
      if (!welcomeSeen && !guideDismissed) {
        // Afficher le guide après un petit délai pour ne pas surcharger l'UI
        const timer = setTimeout(() => {
          setGuideType('welcome');
          setShowGuide(true);
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [profile, loading, guideDismissed]);

  // 💜 Afficher le guide pour les nouveaux profils aussi !
  useEffect(() => {
    if (!loading && !profile && customerId && !guideDismissed) {
      // Nouvel utilisateur sans profil → message de bienvenue
      const welcomeSeen = localStorage.getItem('heartbeats_guide_welcome_seen');
      if (!welcomeSeen) {
        const timer = setTimeout(() => {
          setGuideType('firstProfile');
          setShowGuide(true);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [loading, profile, customerId, guideDismissed]);

  // Mettre à jour la ref pour loadMatches
  useEffect(() => {
    loadMatchesRef.current = loadMatches;
  }, [loadMatches]);

  // 🔌 Écouter les événements WebSocket (après définition des callbacks)
  useEffect(() => {
    if (!isConnected || !customerId) return;  // 🔧 FIX: Utiliser customerId

    // Écouter les appels entrants via WebSocket
    const handleIncomingCall = (data) => {
      if (!incomingCall && !activeVideoCall) {
        setIncomingCall({
          hasIncomingCall: true,
          callId: data.callId,
          callerId: data.callerId,
          callerName: data.callerName || 'Quelqu\'un',
          matchId: data.matchId
        });
      }
    };

    // Écouter les nouveaux messages
    const handleNewMessage = (data) => {
      if (showChat && data.matchId === showChat.matchId) {
        setMessages(prev => [...prev, data]);
      }
    };

    // Écouter les notifications de typing via WebSocket
    const handleTyping = (data) => {
      if (showChat && data.matchId === showChat.matchId && data.customerId !== customerId) {  // 🔧 FIX
        if (data.isTyping) {
          setTypingStatus(`✍️ ${data.displayName || 'L\'autre'} écrit...`);
        } else {
          setTypingStatus(null);
        }
      }
    };

    // Écouter les notifications de match
    const handleNewMatch = (data) => {
      if (data.customerId === customerId || data.targetId === customerId) {  // 🔧 FIX
        loadMatchesRef.current?.();
        toast.success("💥 Tu as un nouveau match !");
      }
    };

    // Écouter les check-in/check-out
    const handleUserAtStore = () => {
      // On pourrait rafraîchir la liste des utilisateurs au magasin
    };

    const unsubIncoming = socketOn('incoming_call', handleIncomingCall);
    const unsubMessage = socketOn('new_message', handleNewMessage);
    const unsubTyping = socketOn('typing', handleTyping);
    const unsubMatch = socketOn('new_match', handleNewMatch);
    const unsubAtStore = socketOn('user_at_store', handleUserAtStore);

    return () => {
      unsubIncoming?.();
      unsubMessage?.();
      unsubTyping?.();
      unsubMatch?.();
      unsubAtStore?.();
    };
  }, [isConnected, customerId, showChat, incomingCall, activeVideoCall, socketOn]);  // 🔧 FIX

  // Animation confetti pour les matchs
  const triggerMatchConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#ff6b6b', '#ff8e8e', '#ffb4b4', '#ffd4d4', '#ff4757']
    });
  };

  // 👻 MODE INVISIBLE - Toggle Ghost Mode (Premium uniquement)
  const toggleGhostMode = async () => {
    if (!isPremium) {
      setShowPaywall({ feature: 'ghostMode' });
      return;
    }
    
    const newGhostMode = !ghostMode;
    setGhostMode(newGhostMode);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API_URL}/api/heartbeats/profile/${customerId}/settings`, {
        ghostMode: newGhostMode
      });
      
      toast.success(newGhostMode 
        ? "👻 Mode invisible activé ! Tu navigues incognito"
        : "👁️ Mode visible activé ! Tu es de nouveau visible"
      );
    } catch (error) {
      console.error('Erreur toggle ghost mode:', error);
      setGhostMode(!newGhostMode); // Rollback
      toast.error("Erreur lors du changement de mode");
    }
  };

  // 🚀 BOOST PROFIL - Mettre en avant pendant 30 min (Premium)
  const activateBoost = async () => {
    if (!isPremium) {
      setShowPaywall({ feature: 'boost' });
      return;
    }
    
    if (boostActive) {
      toast.info("🚀 Boost déjà actif !");
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${API_URL}/api/heartbeats/profile/${customerId}/boost`);
      
      if (response.data.success) {
        setBoostActive(true);
        const endTime = new Date(Date.now() + 30 * 60 * 1000); // 30 minutes
        setBoostEndTime(endTime);
        
        toast.success("🚀 Boost activé ! Ton profil est mis en avant pour 30 minutes");
        
        // Confetti pour célébrer
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.7 },
          colors: ['#a855f7', '#ec4899', '#06b6d4']
        });
        
        // Désactiver après 30 min
        setTimeout(() => {
          setBoostActive(false);
          setBoostEndTime(null);
          toast.info("⏱️ Boost terminé ! Tu peux en activer un nouveau");
        }, 30 * 60 * 1000);
      }
    } catch (error) {
      console.error('Erreur activation boost:', error);
      toast.error("Erreur lors de l'activation du boost");
    }
  };

  // Envoyer un crush
  const sendCrush = async (targetId) => {
    try {
      const result = await api.sendHeartbeatsCrush(customer.id, targetId);
      if (result.isMatch) {
        setShowMatch(result.matchedProfile);
        triggerMatchConfetti();
        toast.success("💥 C'est un MATCH !");
        loadMatches();
      } else {
        toast.success("💘 Crush envoyé en secret !");
      }
      loadCrushes();
      goToNextProfile();
    } catch (error) {
      toast.error("Erreur lors de l'envoi du crush");
    }
  };

  // Passer au profil suivant
  const goToNextProfile = () => {
    if (profiles.length > 0) {
      nextProfile(); // Utilise la fonction du store Zustand
    }
    setIcebreaker(ICEBREAKERS[Math.floor(Math.random() * ICEBREAKERS.length)]);
  };

  // Profil précédent
  const goToPrevProfile = () => {
    // Note: Le store Zustand gère currentProfileIndex, mais pour le prev on doit le faire manuellement
    const newIndex = (currentIndex - 1 + profiles.length) % Math.max(profiles.length, 1);
    useHeartbeatsStore.setState({ currentProfileIndex: newIndex });
  };

  // Toggle visibilité du profil
  const toggleVisibility = async () => {
    try {
      await api.updateHeartbeatsProfile(customer.id, { isVisible: !isVisible });
      setIsVisible(!isVisible);
      toast.success(isVisible ? "Profil masqué 👻" : "Profil visible ! 🔥");
      loadProfiles();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Toggle présence au magasin - utilise WebSocket si disponible
  const toggleAtStore = async () => {
    try {
      const newAtStore = !atStore;
      await api.updateHeartbeatsProfile(customer.id, { atStore: newAtStore });
      setAtStore(newAtStore);
      
      // Notifier via WebSocket si connecté
      if (isConnected) {
        if (newAtStore) {
          checkinStore();
        } else {
          checkoutStore();
        }
      }
      
      toast.success(atStore ? "Tu as quitté le magasin" : "📍 Tu es au magasin !");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Sauvegarder le profil
  const saveProfile = async (profileData) => {
    try {
      await api.updateHeartbeatsProfile(customer.id, profileData);
      setProfile({ ...profile, ...profileData });
      setShowSetup(false);
      
      // 🌌 ÉTAPE 2 : Vérifier si les passions sont complètes
      const hasPassions = profileData.passions && Object.values(profileData.passions).flat().length >= 3;
      
      if (!hasPassions) {
        // Afficher le modal des Passions Profondes
        toast.success("Super ! Ton profil de base est créé 🔥");
        setTimeout(() => {
          setShowPassionsModal(true);
        }, 500);
      } else {
        toast.success("Profil mis à jour ! 🔥");
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#a855f7', '#ec4899', '#f43f5e']
        });
      }
      
      loadProfiles();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };
  
  // 🌌 Sauvegarder les Passions Profondes (Étape 2)
  const savePassions = async (passionsData) => {
    try {
      await api.updateHeartbeatsProfile(customer.id, passionsData);
      setProfile({ ...profile, ...passionsData });
      setShowPassionsModal(false);
      
      toast.success("🌟 Passions enregistrées ! Tu es prêt(e) à matcher !");
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.5 },
        colors: ['#a855f7', '#ec4899', '#f43f5e', '#fbbf24']
      });
      
      loadProfiles();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde des passions");
    }
  };

  // 🚫 VÉRIFICATION PASSIONS - Bloque la navigation tant que le profil n'est pas 100% complet
  // Exige : 3+ passions ET 5/5 questions répondues
  const TOTAL_SOUL_QUESTIONS = 5; // Doit correspondre à SOUL_QUESTIONS.length
  
  const hasCompletedPassions = () => {
    if (!profile) return false;
    
    // Vérifier les passions (min 3)
    const passions = profile.passions;
    if (!passions) return false;
    const totalPassions = Object.values(passions).flat().length;
    if (totalPassions < 3) return false;
    
    // Vérifier les questions de l'âme (100% = 5/5)
    const soulAnswers = profile.soulAnswers;
    if (!soulAnswers) return false;
    const totalAnswers = Object.keys(soulAnswers).length;
    if (totalAnswers < TOTAL_SOUL_QUESTIONS) return false;
    
    return true;
  };

  // 🔒 NAVIGATION SÉCURISÉE - Vérifie si l'utilisateur peut changer d'onglet
  const handleTabChange = (newTab) => {
    // Si le profil existe mais n'est pas complet à 100%
    if (profile && !hasCompletedPassions()) {
      // Afficher le modal des passions
      setShowPassionsModal(true);
      
      // Message adapté selon ce qui manque
      const totalPassions = profile.passions ? Object.values(profile.passions).flat().length : 0;
      const totalAnswers = profile.soulAnswers ? Object.keys(profile.soulAnswers).length : 0;
      
      if (totalPassions < 3) {
        toast.info("💜 Sélectionne au moins 3 passions !", {
          description: `${totalPassions}/3 passions sélectionnées`
        });
      } else if (totalAnswers < TOTAL_SOUL_QUESTIONS) {
        toast.info("🔮 Réponds aux questions de l'âme !", {
          description: `${totalAnswers}/${TOTAL_SOUL_QUESTIONS} questions répondues`
        });
      }
      return; // Bloquer la navigation
    }
    // Navigation autorisée
    setActiveTab(newTab);
  };

  // Handler pour mise à jour du profil (utilisé par AtStoreTab)
  const handleUpdateProfile = async (updatedData) => {
    try {
      await api.updateHeartbeatsProfile(customer.id, updatedData);
      setProfile({ ...profile, ...updatedData });
      toast.success("Profil mis à jour ! 💜");
      // Recharger le profil pour s'assurer de la cohérence
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Envoyer un message
  const sendMessage = async (matchId) => {
    if (!newMessage.trim()) return;
    try {
      await api.sendHeartbeatsMessage(customer.id, matchId, newMessage);
      setNewMessage('');
      // Recharger les messages
      const msgs = await api.getHeartbeatsMessages(customer.id, matchId);
      setMessages(msgs || []);
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    }
  };

  // Ouvrir le chat avec un match
  const openChat = async (match) => {
    setShowChat(match);
    try {
      const msgs = await api.getHeartbeatsMessages(customer.id, match.matchId);
      setMessages(msgs || []);
    } catch (error) {
      console.error('Erreur chargement messages');
    }
  };

  // 🔐 COFFRE SPATIAL - Afficher un indicateur de chargement pendant la vérification
  if (vaultLoading) {
    return (
      <div 
        className="flex flex-col items-center justify-center p-12 rounded-2xl"
        style={{
          minHeight: '400px',
          background: isDarkMode 
            ? 'linear-gradient(135deg, #1a0033 0%, #0a0015 50%, #1a0033 100%)'
            : 'linear-gradient(135deg, #f8f4ff 0%, #f0e8ff 50%, #f8f4ff 100%)',
        }}
      >
        {/* Animation de cœur pulsant avec orbite */}
        <div className="relative mb-6">
          {/* Orbite extérieure */}
          <div 
            className="absolute inset-0 w-24 h-24 rounded-full border-2 border-pink-500/30"
            style={{
              animation: 'spin 3s linear infinite',
            }}
          >
            <div 
              className="absolute -top-1 left-1/2 w-2 h-2 bg-pink-500 rounded-full"
              style={{ transform: 'translateX(-50%)' }}
            />
          </div>
          {/* Cœur central */}
          <Heart 
            className="w-12 h-12 text-pink-500 relative z-10" 
            style={{
              animation: 'pulse 1.5s ease-in-out infinite',
              filter: 'drop-shadow(0 0 10px rgba(236, 72, 153, 0.5))',
            }}
          />
        </div>
        
        {/* Texte de chargement */}
        <p className={`text-lg font-medium ${isDarkMode ? 'text-pink-300' : 'text-pink-600'}`}>
          Ouverture du coffre spatial...
        </p>
        <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Vérification de votre identité 🔐
        </p>
        
        {/* Barre de progression animée */}
        <div className="w-48 h-1 bg-gray-700/30 rounded-full mt-4 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 rounded-full"
            style={{
              width: '100%',
              animation: 'shimmer 1.5s ease-in-out infinite',
              backgroundSize: '200% 100%',
            }}
          />
        </div>
        
        {/* Style pour les animations */}
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>
      </div>
    );
  }

  // 🔐 COFFRE SPATIAL - Afficher le coffre si non déverrouillé (AVANT la vérification d'âge)
  if (!vaultUnlocked && !vaultLoading) {
    return (
      <HeartbeatsSpaceVault
        isOpen={showVault}
        onUnlock={handleVaultUnlock}
        onClose={onClose}
        customer={customer}
        isDarkMode={isDarkMode}
        existingBirthDate={customer?.birthDate}
      />
    );
  }

  // Si pas majeur (après vérification vault), afficher message
  if (!isAdult()) {
    return (
      <div className={`p-6 rounded-2xl text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <AlertTriangle className="w-16 h-16 mx-auto text-orange-500 mb-4" />
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Réservé aux +18 ans
        </h3>
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
          Cette fonctionnalité est réservée aux clients majeurs.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div 
        className="flex flex-col items-center justify-center p-12"
        style={{
          // 🔧 FIX: Même hauteur minimale que le module chargé pour éviter les sauts
          minHeight: '400px',
          transition: 'opacity 0.3s ease-out',
        }}
      >
        <Heart className="w-12 h-12 text-pink-500 animate-pulse mb-4" />
        <p className="text-gray-400 text-sm mb-4">Chargement de ton univers...</p>
        {/* Bouton de secours après 5 secondes */}
        <button
          onClick={() => setLoading(false)}
          className="text-xs text-gray-500 hover:text-pink-400 underline opacity-0 animate-fade-in"
          style={{ animationDelay: '5s', animationFillMode: 'forwards' }}
        >
          Forcer l'affichage
        </button>
        <style>{`
          @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
          .animate-fade-in { animation: fade-in 0.5s ease-in; }
        `}</style>
      </div>
    );
  }

  const currentProfile = profiles[currentIndex];
  
  // Sécurité : s'assurer que les tableaux sont toujours des tableaux
  const safeMatches = Array.isArray(matches) ? matches : [];
  const safeReceivedCrushes = Array.isArray(receivedCrushes) ? receivedCrushes : [];
  const safeCrushes = Array.isArray(crushes) ? crushes : [];
  const safeProfiles = Array.isArray(profiles) ? profiles : [];

  return (
    <div className={`heartbeats-module relative rounded-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl group`}
      style={{
        // ✅ FIX macOS/iOS: Layout stable sans saut
        maxHeight: '100dvh',
        height: 'auto',
        minHeight: '400px', // 🔧 FIX: Hauteur minimale pour éviter les tremblements
        width: '100%',
        maxWidth: '100%',
        position: 'relative',
        // ✅ Masquer les scrollbars et empêcher le saut
        overflow: 'hidden',
        // ✅ FIX iOS: Safe area pour iPhone avec encoche
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // ✅ FIX iOS/macOS: Empêcher le "bounce" scroll
        overscrollBehavior: 'contain',
        // ✅ Stabiliser les animations - IMPORTANT pour éviter les tremblements
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        willChange: 'auto', // Désactiver will-change pour éviter les tremblements
        // 🔧 FIX: Transition fluide pour éviter les sauts visuels
        transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
      }}
    >
      {/* ✨ EFFET BORDURE SIMPLE - Sans animations lourdes */}
      <div 
        className="absolute -inset-[2px] rounded-2xl z-0"
        style={{
          background: 'linear-gradient(135deg, #f43f5e, #ec4899, #8b5cf6)',
          opacity: 0.8,
        }}
      />
      {/* Fond intérieur */}
      <div className={`absolute inset-[3px] rounded-xl z-10 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} />
      {/* Contenu */}
      <div className="relative z-20">
      {/* Header avec gradient Heartbeats VIOLET/ROSE + effets lumineux */}
      <div className="heartbeats-header relative bg-gradient-to-r from-purple-600 via-pink-500 to-rose-500 p-6 pt-8 sm:p-6 sm:pt-8 overflow-hidden rounded-t-xl">
        {/* Animated background hearts - Réduit pour performance */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="absolute text-white/20"
              style={{
                left: `${10 + i * 12}%`,
                fontSize: `${16 + (i % 3) * 8}px`,
                animation: `heartbeat-float ${4 + i * 0.5}s ease-in-out infinite`,
                animationDelay: `${i * 0.4}s`,
              }}
            >
              💜
            </div>
          ))}
        </div>
        {/* Rayon de lumière qui traverse */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, white 50%, transparent 100%)',
            animation: 'light-sweep 4s ease-in-out infinite',
            transform: 'skewX(-20deg)',
          }}
        />
        {/* Glow effect */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 30% 50%, rgba(168, 85, 247, 0.4) 0%, transparent 50%)',
          }}
        />
        <style>{`
          @keyframes heartbeat-float {
            0%, 100% { transform: translateY(80px) scale(1); opacity: 0; }
            20% { opacity: 0.6; }
            50% { transform: translateY(-10px) scale(1.2); opacity: 0.8; }
            80% { opacity: 0.6; }
          }
          @keyframes light-sweep {
            0% { transform: translateX(-200%) skewX(-20deg); }
            100% { transform: translateX(400%) skewX(-20deg); }
          }
          @keyframes heartbeat-pulse {
            0%, 100% { transform: scale(1); }
            25% { transform: scale(1.1); }
            50% { transform: scale(1); }
            75% { transform: scale(1.05); }
          }
        `}</style>
        
        {/* === HEADER PRINCIPAL === */}
        <div className="relative">
          {/* Ligne 1: Titre + Actions rapides */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  animation: 'heartbeat-pulse 1.5s ease-in-out infinite'
                }}
              >
                <span className="text-2xl">💜</span>
              </div>
              <div>
                <h2 className="text-xl font-black text-white drop-shadow-lg">Heartbeats</h2>
                <p className="text-white/80 text-sm font-medium">✨ Et mon cœur bat plus fort ✨</p>
              </div>
            </div>
            
            {/* Actions rapides à droite */}
            <div className="flex items-center gap-1">
              {/* 💎 BOUTON PREMIUM - Visible si pas Premium */}
              {!isPremium && (
                <motion.button 
                  onClick={() => setShowPaywall({ feature: null })} 
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  animate={{ 
                    boxShadow: ['0 0 10px rgba(168,85,247,0.5)', '0 0 20px rgba(168,85,247,0.8)', '0 0 10px rgba(168,85,247,0.5)']
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  data-testid="btn-go-premium"
                >
                  <Sparkles className="w-3 h-3" />
                  Premium
                </motion.button>
              )}
              
              {/* 👑 BOUTON COURONNES - Plus visible avec animation */}
              <motion.button 
                onClick={() => setShowCrownShop(true)} 
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/30 hover:border-yellow-400/50 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Acheter des Couronnes"
                data-testid="btn-buy-crowns"
              >
                <Crown className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-bold text-yellow-300">{crowns || 0}</span>
                <span className="text-yellow-400/60 text-xs">+</span>
              </motion.button>
              
              <Button variant="ghost" size="sm" onClick={() => setShowHBTShop(true)} className="text-white hover:bg-white/20" title="Wallet HBT">
                <Wallet className="w-4 h-4 text-purple-300" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowConfidentialSettings(true)} className={`text-white hover:bg-white/20 ${isConfidentialMode ? 'bg-purple-500/40' : ''}`} data-testid="btn-confidential-mode" title="Mode Confidentiel">
                {isConfidentialMode ? '👻' : '🔓'}
              </Button>
            </div>
          </div>
          
          {/* === LIGNE 2: MON PROFIL (bien visible!) === */}
          <motion.div 
            className="flex items-center gap-4 p-4 rounded-2xl mb-4 cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => setShowMyProfile(true)}
            data-testid="btn-my-profile"
          >
            {/* Photo / Avatar / Initiales - AGRANDI */}
            <div className="relative">
              <div 
                className="w-20 h-20 rounded-2xl overflow-hidden shadow-xl ring-3 ring-pink-400/60"
                style={{ minWidth: '80px', minHeight: '80px' }}
              >
                {profile?.photos?.[0] ? (
                  // Photo du profil Heartbeats
                  <img 
                    src={profile.photos[0]} 
                    alt="Mon profil"
                    className="w-full h-full object-cover"
                  />
                ) : profile?.avatarImage && (profile.avatarImage.startsWith('data:') || profile.avatarImage.startsWith('http')) ? (
                  // Avatar IA généré (image base64 ou URL)
                  <img 
                    src={profile.avatarImage} 
                    alt="Mon avatar"
                    className="w-full h-full object-cover"
                  />
                ) : customer?.profilePhoto ? (
                  // 📸 Photo importée depuis l'espace client !
                  <img 
                    src={customer.profilePhoto} 
                    alt="Ma photo"
                    className="w-full h-full object-cover"
                  />
                ) : profile?.avatar && (profile.avatar.startsWith('data:') || profile.avatar.startsWith('http')) ? (
                  // Avatar IA (ancien champ - vérifier que c'est une image)
                  <img 
                    src={profile.avatar} 
                    alt="Mon avatar"
                    className="w-full h-full object-cover"
                  />
                ) : profile?.avatar && !profile.avatar.startsWith('data:') && !profile.avatar.startsWith('http') ? (
                  // Avatar emoji (pas une URL/base64)
                  <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-4xl">
                    {profile.avatar}
                  </div>
                ) : profile?.avatarEmoji ? (
                  // Avatar emoji explicite
                  <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-4xl">
                    {profile.avatarEmoji}
                  </div>
                ) : (
                  // Initiales par défaut
                  <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                    <span className="text-3xl font-black text-white">
                      {customer?.firstName?.[0] || customer?.name?.[0] || '?'}
                    </span>
                  </div>
                )}
              </div>
              {/* Badge en ligne */}
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse" />
              </div>
              {/* 📸 Bouton upload rapide - 1 clic pour ajouter une photo ! */}
              {(!profile?.photos?.length && !profile?.avatarImage) && (
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    // Ouvrir directement le modal de setup sur l'onglet photos
                    setShowSetup(true); 
                  }}
                  className="absolute -top-1 -left-1 w-7 h-7 bg-pink-500 rounded-full border-2 border-white flex items-center justify-center shadow-lg hover:bg-pink-600 transition-colors animate-bounce"
                  title="Ajouter une photo"
                >
                  <Camera className="w-4 h-4 text-white" />
                </button>
              )}
            </div>
            
            {/* Infos profil + ACCROCHE */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white font-bold text-xl">{profile?.name || customer?.firstName || 'Moi'}</span>
                {profile?.isVerified && <Shield className="w-5 h-5 text-blue-400" />}
              </div>
              
              {/* 💬 ACCROCHE - La phrase qui définit ton mood ! */}
              {profile?.tagline ? (
                <p className="text-white/90 text-sm italic mt-1 line-clamp-2">
                  "{profile.tagline}"
                </p>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowTaglineEditor(true); }}
                  className="text-pink-300 text-sm mt-1 hover:text-pink-200 transition-colors flex items-center gap-1"
                >
                  <Sparkles className="w-3 h-3" />
                  Ajoute ton accroche magique...
                </button>
              )}
              
              <p className="text-white/50 text-xs mt-1">
                {profile?.photos?.length ? `${profile.photos.length} tof${profile.photos.length > 1 ? 's' : ''} 📸` : '📸 Balance une tof !'}
              </p>
            </div>
            
            {/* Bouton modifier */}
            <div className="flex flex-col items-center gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="bg-white/20 text-white hover:bg-white/30 rounded-full px-5 py-2"
                onClick={(e) => { e.stopPropagation(); setShowSetup(true); }}
              >
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            </div>
          </motion.div>
          
          {/* === BARRE D'ACTIONS RAPIDES === */}
          {/* Ordre: Visible → Salons → Cadeaux → Ombre → Vérifier → Revenus */}
          <div className="flex items-center justify-center gap-1 flex-wrap mt-2 mb-2">
            
            {/* 1. 👁️ Toggle visibilité */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleVisibility}
              className={`text-white hover:bg-white/20 ${isVisible ? 'bg-green-500/20' : 'bg-red-500/20'}`}
              title={isVisible ? 'Profil visible' : 'Profil masqué'}
            >
              {isVisible ? <Eye className="w-5 h-5 text-green-400" /> : <EyeOff className="w-5 h-5 text-red-400" />}
              <span className={`ml-1 text-xs ${isVisible ? 'text-green-300' : 'text-red-300'}`}>
                {isVisible ? 'Visible' : 'Masqué'}
              </span>
            </Button>
            
            {/* 2. 🗣️ Salons Thématiques */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowThemedRooms(true)}
              className="text-white hover:bg-white/20"
              data-testid="btn-themed-rooms"
              title="Salons Thématiques"
            >
              <MessageCircle className="w-5 h-5 text-cyan-300" />
              <span className="ml-1 text-xs text-cyan-300">Salons</span>
            </Button>
            
            {/* 3. 🎁 Mes Cadeaux Reçus */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowMyGifts(true)}
              className="text-white hover:bg-white/20"
              data-testid="btn-my-gifts"
              title="Mes cadeaux reçus"
            >
              <Gift className="w-5 h-5 text-pink-300" />
              <span className="ml-1 text-xs text-pink-300">Cadeaux</span>
            </Button>
            
            {/* 4. 🖤 Shadow Mode */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowShadowMode(true)}
              className="text-white hover:bg-white/20 group"
              data-testid="btn-shadow-mode"
              title="🖤 Shadow Mode - Assume ton ombre"
            >
              <motion.div animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                <Moon className="w-5 h-5 text-purple-400 group-hover:text-purple-300" />
              </motion.div>
              <span className="ml-1 text-xs text-purple-300">Ombre</span>
            </Button>
            
            {/* 5. ✓ Vérification profil */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowVerificationModal(true)}
              className={`text-white hover:bg-white/20 ${profile?.isVerified ? 'bg-blue-500/30' : ''}`}
              title={profile?.isVerified ? 'Profil vérifié ✓' : 'Vérifier mon profil'}
            >
              <Shield className={`w-5 h-5 ${profile?.isVerified ? 'text-blue-400' : 'text-gray-400'}`} />
              {profile?.isVerified && <span className="ml-1 text-xs text-blue-300">Vérifié ✓</span>}
              {!profile?.isVerified && <span className="ml-1 text-xs text-gray-400">Vérifier</span>}
            </Button>
            
            {/* 6. 💰 Programme Affiliation - Revenus */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAffiliation(true)}
              className="text-white hover:bg-white/20"
              data-testid="btn-affiliation"
              title="Programme Affiliation Premium"
            >
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              <span className="ml-1 text-xs text-emerald-300">Revenus</span>
            </Button>
          </div>
        </div>

        {/* Onglets - RESPONSIVE: wrap sur desktop, scroll sur mobile */}
        <div className="relative">
          {/* Flèches - UNIQUEMENT SUR MOBILE */}
          <button
            onClick={() => {
              const container = document.querySelector('.heartbeats-tabs-scroll');
              if (container) container.scrollBy({ left: -200, behavior: 'smooth' });
            }}
            className="lg:hidden absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-gradient-to-r from-purple-600 to-transparent flex items-center justify-center text-white hover:from-purple-700 transition-all"
            aria-label="Défiler vers la gauche"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          {/* Conteneur des onglets - WRAP sur desktop, SCROLL sur mobile */}
          <div 
            className="heartbeats-tabs-scroll heartbeats-tabs flex gap-2 pb-2 px-10 lg:px-2 lg:flex-wrap lg:justify-center overflow-x-auto lg:overflow-x-visible scroll-smooth scrollbar-hide"
            style={{
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {/* 🏪 COMMUNAUTÉ D'ABORD ! Le magasin = lieu de rencontre RÉEL */}
            {['store', 'online', 'rooms', 'txtofs', 'guides', 'discover', 'matches', 'crushes', 'crossings', 'stories', 'couples', 'viral', 'soul', 'games', 'blind', 'speeddate', 'leaderboard'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab
                    ? 'bg-white text-purple-600 shadow-lg scale-105'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === 'store' && '📍 Au magasin'}
                {tab === 'online' && '🟢 En ligne'}
                {tab === 'rooms' && '🎙️ Salons Vocaux'}
                {tab === 'txtofs' && '💬 Txtofs'}
                {tab === 'guides' && '🌟 Mes Guides'}
                {tab === 'discover' && '💜 Qui va craquer ?'}
                {tab === 'matches' && '💘 Ça a matché !'}
                {tab === 'crushes' && `💜 Ils t'aiment`}
                {tab === 'crossings' && '🗺️ Destins croisés'}
                {tab === 'stories' && '📸 Stories'}
                {tab === 'couples' && '💕 Amis Couples'}
                {tab === 'viral' && '🔥 Hot'}
                {tab === 'soul' && '🌟 Mon Âme'}
                {tab === 'games' && '🎮 Jeux'}
                {tab === 'blind' && '🙈 Blind'}
                {tab === 'speeddate' && '⚡ Coup de foudre'}
                {tab === 'leaderboard' && '👑 Hall of Fame'}
              </button>
            ))}
          </div>
          
          {/* Flèche droite - UNIQUEMENT SUR MOBILE */}
          <button
            onClick={() => {
              const container = document.querySelector('.heartbeats-tabs-scroll');
              if (container) container.scrollBy({ left: 200, behavior: 'smooth' });
            }}
            className="lg:hidden absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-gradient-to-l from-purple-600 to-transparent flex items-center justify-center text-white hover:from-purple-700 transition-all"
            aria-label="Défiler vers la droite"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          
          {/* Indicateur de scroll - UNIQUEMENT SUR MOBILE */}
          <div className="lg:hidden text-center mt-1">
            <span className="text-[10px] text-white/60">← Glisse ou utilise les flèches →</span>
          </div>
        </div>
      </div>

      {/* Contenu - SCROLLABLE ! */}
      <div 
        className={`heartbeats-tab-content p-4 md:p-6 scrollbar-hide ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}
        style={{
          maxHeight: 'calc(100dvh - 320px)',
          minHeight: '400px',
          overflowY: 'auto',
          overflowX: 'hidden',
          // ✅ FIX macOS: Masquer scrollbars et empêcher saut
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          // ✅ Stabiliser
          position: 'relative',
          width: '100%',
        }}
      >
        {/* 📸 Tab Stories - Contenu éphémère 24h */}
        {/* 📸 Tab Stories (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'stories' && (
          <StoriesTab customerId={customer?.id} isDarkMode={isDarkMode} profile={profile} />
        )}

        {/* 🌟 Tab Soul - Mon Âme (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'soul' && (
          <SoulTab customerId={customer?.id} isDarkMode={isDarkMode} />
        )}

        {/* 🔥 Tab Viral (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {/* 🔥 Tab Viral (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'viral' && (
          <ViralTab customerId={customer?.id} isDarkMode={isDarkMode} profile={profile} />
        )}

        {/* 🎮 Tab Games - Mini-jeux Dating (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'games' && (
          <GamesTab customerId={customer?.id} isDarkMode={isDarkMode} />
        )}

        {/* 🙈 Tab Blind Date - Matcher sans voir les photos (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'blind' && (
          <BlindDateTab customerId={customer?.id} isDarkMode={isDarkMode} />
        )}

        {/* 🗺️ Tab Croisements (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'crossings' && (
          <CrossingsTab customerId={customer?.id} isDarkMode={isDarkMode} profile={profile} />
        )}

        {/* ⚡ Tab Speed Dating (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'speeddate' && (
          <SpeedDatingTab customer={customer} isDarkMode={isDarkMode} />
        )}

        {/* 🏆 Tab Leaderboard (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'leaderboard' && (
          <LeaderboardTab customerId={customer?.id} isDarkMode={isDarkMode} />
        )}
        
        {/* 🎙️ Tab Rooms - Salons Vocaux (avec Gate obligatoire) */}
        {activeTab === 'rooms' && (
          <TxtofsGate
            customer={customer}
            profile={profile}
            isDarkMode={isDarkMode}
            onOpenProfileSetup={() => setShowSetup(true)}
          >
            <RoomsTab 
              customerId={customer?.id} 
              customerName={customer?.firstName} 
              isDarkMode={isDarkMode}
              socket={isConnected ? { on: socketOn, off: socketOff, emit: joinMatch } : null}
              isConnected={isConnected}
            />
          </TxtofsGate>
        )}
        
        {/* 💬 Tab Txtofs - Salons Texte + Photos (avec Gate obligatoire) */}
        {activeTab === 'txtofs' && (
          <TxtofsGate
            customer={customer}
            profile={profile}
            isDarkMode={isDarkMode}
            onOpenProfileSetup={() => setShowSetup(true)}
          >
            <TxtofsTab 
              customer={customer}
              profile={profile}
              isDarkMode={isDarkMode}
            />
          </TxtofsGate>
        )}
        
        {/* 🌟 Tab Guides - Chat avec tes guides Heartbeats (GRANDS VISAGES) */}
        {activeTab === 'guides' && (
          <BigFaceGuidesSelector 
            customer={customer}
            profile={profile}
            isDarkMode={isDarkMode}
          />
        )}
        
        {/* 📍 Tab Au Magasin (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'store' && (
          <AtStoreTab 
            customerId={customer?.id}
            isDarkMode={isDarkMode}
            profile={profile}
            onUpdateProfile={handleUpdateProfile}
          />
        )}
        
        {/* 🟢 Tab En ligne (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'online' && (
          <OnlineTab 
            customerId={customer?.id}
            isDarkMode={isDarkMode}
            socket={isConnected ? { on: socketOn, off: socketOff } : null}
            isConnected={isConnected}
            onOpenChat={(user) => {
              // Gérer l'ouverture du chat
            }}
            onSendCrush={(user) => {
              // Gérer l'envoi de crush
            }}
          />
        )}
        
        {/* 🔍 Tab Discover (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'discover' && (
          <DiscoverTab 
            customerId={customer?.id}
            isDarkMode={isDarkMode}
            profile={profile}
            onShowSetup={() => setShowSetup(true)}
            onOpenChat={(matchedProfile) => {
              // Gérer l'ouverture du chat avec un match
            }}
            onSendGift={(targetProfile) => {
              // Gérer l'envoi de cadeau
            }}
          />
        )}

        {/* 💕 Tab Couples - Pour les couples qui cherchent des amis */}
        {activeTab === 'couples' && (
          <CouplesTab 
            customerId={customer?.id}
            isDarkMode={isDarkMode}
            profile={profile}
            onShowSetup={() => setShowSetup(true)}
          />
        )}

        {/* 💌 Tab Matches (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {activeTab === 'matches' && (
          <MatchesTab 
            customerId={customer?.id}
            isDarkMode={isDarkMode}
            matches={safeMatches}
            onOpenChat={openChat}
            onSendGift={(match) => {
              // Gérer l'envoi de cadeau
            }}
            onStartGame={(match) => {
              // Gérer le démarrage d'un jeu
            }}
          />
        )}

        {/* 💜 Tab Crushes reçus (REFACTORISÉ Mozart/Jobs/Tesla) */}
        {/* ✅ Intégration Paywall : isPremium contrôle la visibilité des profils */}
        {activeTab === 'crushes' && (
          <CrushesTab 
            receivedCrushes={safeReceivedCrushes} 
            isDarkMode={isDarkMode} 
            isPremium={isPremium}
            onShowPaywall={setShowPaywall}
          />
        )}
      </div>

      {/* Modal Match */}
      <AnimatePresence>
        {showMatch && (
          <Dialog open={!!showMatch} onOpenChange={() => setShowMatch(null)}>
            <DialogContent className="sm:max-w-md bg-gradient-to-br from-purple-500 to-pink-500 border-0">
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center py-8"
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 0.5 }}
                  className="text-8xl mb-4"
                >
                  💥
                </motion.div>
                <h2 className="text-4xl font-bold text-white mb-2">🔥 BOOM ! C'est un MATCH !</h2>
                <p className="text-white/80 mb-6">
                  Toi et {showMatch.displayName} vous plaisez mutuellement !
                </p>
                <div className="flex justify-center gap-4">
                  <Button
                    onClick={() => {
                      openChat(showMatch);
                      setShowMatch(null);
                    }}
                    className="bg-white text-pink-500 hover:bg-gray-100"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Envoyer un message
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowMatch(null)}
                    className="border-white text-white hover:bg-white/20"
                  >
                    Continuer
                  </Button>
                </div>
              </motion.div>
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>

      {/* Polling du typing status quand le chat est ouvert - désactivé si WebSocket connecté */}
      <TypingStatusEffect 
        showChat={showChat}
        customerId={customer?.id}
        onTypingChange={setTypingStatus}
        isWebSocketConnected={isConnected}
      />

      {/* Modal Chat avec Photos */}
      <Dialog open={!!showChat} onOpenChange={() => { setShowChat(null); setTypingStatus(null); }}>
        <DialogContent className={`sm:max-w-md ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-3 ${isDarkMode ? 'text-white' : ''}`}>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-xl">
                {showChat?.avatar || '😊'}
              </div>
              <div className="flex-1">
                {showChat?.displayName}
                {/* Typing indicator */}
                {typingStatus?.typing && (
                  <div className="text-xs text-pink-500 font-normal animate-pulse">
                    ✍️ {typingStatus.displayName} écrit...
                  </div>
                )}
              </div>
              {/* Bouton cadeau virtuel (couronnes) */}
              <button
                onClick={() => setShowGiftModal({
                  targetId: showChat?.odor || showChat?.customerId,
                  targetName: showChat?.displayName
                })}
                className={`p-2 rounded-full transition-colors ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
                title="Envoyer un cadeau virtuel (couronnes)"
                data-testid="gift-button"
              >
                <Gift className="w-5 h-5" />
              </button>
              {/* 💎 Bouton Cadeau HBT Prestige */}
              <button
                onClick={() => {
                  setGiftTargetProfile({
                    customerId: showChat?.odor || showChat?.customerId,
                    displayName: showChat?.displayName
                  });
                  setShowHBTShop(true);
                }}
                className={`p-2 rounded-full transition-colors bg-gradient-to-r from-cyan-500 to-purple-500 text-white hover:from-cyan-600 hover:to-purple-600 animate-pulse`}
                title="💎 Cadeau HBT Prestige"
                data-testid="hbt-gift-button"
              >
                <Sparkles className="w-5 h-5" />
              </button>
              {/* Bouton Offrir du Champagne 🍾 (vrai cadeau boutique) */}
              <button
                onClick={() => setShowGiftShopModal({
                  recipientId: showChat?.odor || showChat?.customerId,
                  recipientName: showChat?.displayName
                })}
                className={`p-2 rounded-full transition-colors bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600`}
                title="Offrir un vrai cadeau (Champagne, etc.)"
                data-testid="real-gift-button"
              >
                <Wine className="w-5 h-5" />
              </button>
              {/* Bouton appel vidéo */}
              <button
                onClick={() => setActiveVideoCall({
                  matchId: showChat?.matchId,
                  partnerName: showChat?.displayName,
                  partnerAvatar: showChat?.avatar
                })}
                className={`p-2 rounded-full transition-colors ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
                title="Appel vidéo"
                data-testid="video-call-button"
              >
                <Video className="w-5 h-5" />
              </button>
              {showChat?.atStore && (
                <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                  Au magasin
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className={`h-64 overflow-y-auto p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <MessageCircle className={`w-12 h-12 mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                  Commence la conversation ! 👋
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg, idx) => (
                  <MessageBubble 
                    key={idx}
                    msg={msg}
                    isDarkMode={isDarkMode}
                    customerId={customer?.id}
                    onPhotoResponse={async (photoId, accept) => {
                      try {
                        await api.respondToHeartbeatsPhoto(customer.id, photoId, accept);
                        toast.success(accept ? '📷 Photo acceptée !' : '❌ Photo refusée');
                        // Recharger les messages
                        const msgs = await api.getHeartbeatsMessages(customer.id, showChat.matchId);
                        setMessages(msgs || []);
                      } catch (error) {
                        toast.error("Erreur lors de la réponse");
                      }
                    }}
                    onReportPhoto={async (photoId) => {
                      try {
                        await api.reportHeartbeatsPhoto(customer.id, photoId, "Contenu inapproprié");
                        toast.success('🚩 Photo signalée');
                        const msgs = await api.getHeartbeatsMessages(customer.id, showChat.matchId);
                        setMessages(msgs || []);
                      } catch (error) {
                        toast.error("Erreur lors du signalement");
                      }
                    }}
                    onDeleteMessage={async (messageId) => {
                      try {
                        await api.deleteHeartbeatsMessage(customer.id, messageId);
                        toast.success('🗑️ Message supprimé');
                        // Recharger les messages
                        const msgs = await api.getHeartbeatsMessages(customer.id, showChat.matchId);
                        setMessages(msgs || []);
                      } catch (error) {
                        toast.error("Erreur lors de la suppression");
                      }
                    }}
                    onMessageRead={async (messageId) => {
                      try {
                        const result = await api.markHeartbeatsMessageRead(messageId, customer.id);
                        if (result.ephemeralDeleted) {
                          // Message éphémère supprimé, recharger
                          setTimeout(async () => {
                            const msgs = await api.getHeartbeatsMessages(customer.id, showChat.matchId);
                            setMessages(msgs || []);
                          }, 2000);
                        }
                      } catch (error) {
                        console.error("Erreur marquage lu:", error);
                      }
                    }}
                    onReaction={async (messageId, reaction) => {
                      try {
                        if (reaction) {
                          await api.addHeartbeatsMessageReaction(messageId, customer.id, reaction);
                          toast.success(`${reaction} Réaction ajoutée !`);
                        } else {
                          await api.removeHeartbeatsMessageReaction(messageId, customer.id);
                        }
                        // Recharger les messages pour voir la réaction
                        const msgs = await api.getHeartbeatsMessages(customer.id, showChat.matchId);
                        setMessages(msgs || []);
                      } catch (error) {
                        toast.error("Erreur lors de la réaction");
                      }
                    }}
                    onReply={(msg) => {
                      // Ajouter le nom de l'auteur si c'est pas moi
                      const msgWithAuthor = {
                        ...msg,
                        authorName: msg.fromMe ? 'Toi' : showChat?.displayName || 'Ton match'
                      };
                      setReplyingTo(msgWithAuthor);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* 🎮 Barre d'actions Chat */}
          <div className={`flex items-center gap-2 p-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowGameLobby({ partnerId: showChat?.partnerId, partnerName: showChat?.displayName })}
              className="text-xs"
            >
              🎮 Jouer
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowIcebreakers({ customerId: showChat?.partnerId })}
              className="text-xs"
            >
              🧚‍♀️ Pixie
            </Button>
          </div>
          
          {/* Barre d'options avancées */}
          <ChatInputBar 
            isDarkMode={isDarkMode}
            customerId={customer?.id}
            matchId={showChat?.matchId}
            replyingTo={replyingTo}
            onCancelReply={() => setReplyingTo(null)}
            onMessageSent={async () => {
              setReplyingTo(null);
              const msgs = await api.getHeartbeatsMessages(customer.id, showChat.matchId);
              setMessages(msgs || []);
            }}
            isWebSocketConnected={isConnected}
            onTypingStart={() => socketStartTyping(showChat?.matchId)}
            onTypingStop={() => socketStopTyping(showChat?.matchId)}
          />
        </DialogContent>
      </Dialog>

      {/* Modal Setup Profil */}
      <ProfileSetupModal
        isOpen={showSetup}
        onClose={() => setShowSetup(false)}
        onSave={saveProfile}
        customer={customer}
        existingProfile={profile}
        isDarkMode={isDarkMode}
      />
      
      {/* 🌌 Modal Passions Profondes (Étape 2) */}
      <PassionsSetupModal
        isOpen={showPassionsModal}
        onClose={() => setShowPassionsModal(false)}
        onSave={savePassions}
        existingPassions={profile?.passions || {}}
        existingSoulAnswers={profile?.soulAnswers || {}}
        isDarkMode={isDarkMode}
        displayName={profile?.displayName || customer?.firstName || 'toi'}
      />

      {/* Modal Appel Vidéo Sortant */}
      {activeVideoCall && (
        <VideoCallModal
          isDarkMode={isDarkMode}
          customerId={customer?.id}
          matchId={activeVideoCall.matchId}
          partnerName={activeVideoCall.partnerName}
          partnerAvatar={activeVideoCall.partnerAvatar}
          onClose={() => setActiveVideoCall(null)}
        />
      )}

      {/* Modal Appel Vidéo Entrant */}
      {incomingCall && (
        <IncomingCallModal
          isDarkMode={isDarkMode}
          callData={incomingCall}
          customerId={customer?.id}
          onAccept={() => {
            setIncomingCall(null);
            // L'appel est géré dans le modal
          }}
          onReject={() => setIncomingCall(null)}
        />
      )}

      {/* Modal Cadeaux */}
      {showGiftModal && (
        <Dialog open={true} onOpenChange={() => setShowGiftModal(null)}>
          <DialogContent className={`sm:max-w-md ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white'}`}>
            <GiftCatalogPanel
              isDarkMode={isDarkMode}
              customerId={customer?.id}
              targetId={showGiftModal.targetId}
              targetName={showGiftModal.targetName}
              onClose={() => setShowGiftModal(null)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Modal Super Like 💎 */}
      {showSuperLikeModal && (
        <Dialog open={true} onOpenChange={() => setShowSuperLikeModal(null)}>
          <DialogContent className={`sm:max-w-md ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white'}`}>
            <SuperLikeModal
              isDarkMode={isDarkMode}
              customerId={customer?.id}
              targetId={showSuperLikeModal.targetId}
              targetName={showSuperLikeModal.targetName}
              targetAvatar={showSuperLikeModal.targetAvatar}
              onClose={() => setShowSuperLikeModal(null)}
              onSuccess={(result) => {
                if (result.isMatch) {
                  loadMatches();
                }
                goToNextProfile();
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Modal Vérification */}
      <VerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        customerId={customer?.id}
        isDarkMode={isDarkMode}
      />

      {/* 💰 Boutique Couronnes Stripe */}
      <CrownShopStripe
        isOpen={showCrownShop}
        onClose={() => setShowCrownShop(false)}
        customerId={customer?.id}
        customerEmail={customer?.email}
        currentCrowns={crowns || 0}
      />

      {/* 🔒 Paywall Premium */}
      <PaywallModal
        isOpen={!!showPaywall}
        onClose={() => setShowPaywall(null)}
        feature={showPaywall?.feature}
        customerId={customerId || customer?.id}
        customerEmail={profile?.email || customer?.email}
      />

      {/* 🍾 Boutique Cadeaux Boutique (Champagne, etc.) */}
      {showGiftShopModal && (
        <GiftShop
          isOpen={true}
          onClose={() => setShowGiftShopModal(null)}
          customerId={customer?.id}
          recipientId={showGiftShopModal?.recipientId}
          recipientName={showGiftShopModal?.recipientName}
          onSuccess={() => {
            toast.success('🎁 Cadeau envoyé !');
          }}
        />
      )}

      {/* 🎟️ Mes Cadeaux Reçus */}
      <MyGiftVouchers
        isOpen={showMyGifts}
        onClose={() => setShowMyGifts(false)}
        customerId={customer?.id}
      />
      
      {/* 💜 LUNA & ENZO - Les guides Heartbeats avec GRANDS VISAGES */}
      <AnimatePresence>
        {showGuide && !guideDismissed && (
          <BigFaceGuideChat
            guide={guideCharacter}
            customerName={extractFirstName(customer?.name || customer?.firstName)}
            isDarkMode={isDarkMode}
            onAction={(action) => {
              // 🚀 Gérer les actions des guides
              console.log('Guide action:', action);
              switch(action) {
                case 'start_quiz':
                case 'start_profile':
                case 'start':
                case 'createProfile':
                  if (!profile || !profile.customerId || !profile.bio) {
                    setShowGuide(false);
                    setGuideDismissed(true);
                    setTimeout(() => setShowSetup(true), 300);
                  }
                  break;
                case 'open_salons':
                  setShowGuide(false);
                  setGuideDismissed(true);
                  handleTabChange('txtofs');
                  break;
                case 'viewMatch':
                  handleTabChange('matches');
                  break;
                case 'viewProfiles':
                  handleTabChange('discover');
                  break;
                case 'viewLikes':
                  handleTabChange('crushes');
                  break;
                case 'sendMessage':
                  // Ouvrir le chat si on a un match
                  if (matches?.length > 0) {
                    handleOpenChat(matches[0]);
                  }
                  break;
                case 'close':
                  setShowGuide(false);
                  setGuideDismissed(true);
                  break;
                default:
                  break;
              }
            }}
            onClose={() => {
              setShowGuide(false);
              setGuideDismissed(true);
              localStorage.setItem(`heartbeats_guide_${guideType}_seen_${customer?.id}`, 'true');
            }}
          />
        )}
      </AnimatePresence>
      
      {/* 💎 HBT TOKEN SHOP */}
      <HBTShopModal
        isOpen={showHBTShop}
        onClose={() => {
          setShowHBTShop(false);
          setGiftTargetProfile(null);
        }}
        customerId={customer?.id}
        targetProfile={giftTargetProfile}
        isDarkMode={isDarkMode}
      />
      
      {/* 🎁 Cadeau HBT Reçu */}
      {receivedGift && (
        <GiftReceivedModal
          isOpen={true}
          onClose={() => setReceivedGift(null)}
          gift={receivedGift.giftInfo}
          senderName={receivedGift.senderName || 'Quelqu\'un'}
          message={receivedGift.message}
          isDarkMode={isDarkMode}
        />
      )}
      
      {/* 🗣️ SALONS THÉMATIQUES */}
      <Dialog open={showThemedRooms} onOpenChange={setShowThemedRooms}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-x-hidden overflow-y-auto ${
          isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white text-gray-900'
        }`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🗣️</span>
              Salons Thématiques
              <span className={`text-sm font-normal ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                - Trouve ta tribu ! ✨
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[70vh] pr-2">
            {activeRoom ? (
              <RoomChatView
                room={activeRoom}
                messages={[]}
                onSendMessage={(msg) => {
                  toast.success('Message envoyé ! (Demo)');
                }}
                onClose={() => setActiveRoom(null)}
                currentUser={{ id: customer?.id, name: profile?.displayName }}
                isDarkMode={isDarkMode}
              />
            ) : (
              <RoomsList
                onRoomSelect={(room) => {
                  setActiveRoom(room);
                  if (!joinedRooms.includes(room.id)) {
                    setJoinedRooms([...joinedRooms, room.id]);
                    toast.success(`🎉 Tu as rejoint "${room.name}" !`);
                  }
                }}
                isDarkMode={isDarkMode}
                userPassions={profile?.passions || {}}
                joinedRooms={joinedRooms}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* 🖤 SHADOW MODE - Les 7 Péchés Capitaux */}
      <ShadowMode
        isOpen={showShadowMode}
        onClose={() => setShowShadowMode(false)}
        customerId={customer?.id}
        customer={customer}
        profile={profile}
        isDarkMode={isDarkMode}
      />
      
      {/* 👤 MON PROFIL - Vue dédiée */}
      <Dialog open={showMyProfile} onOpenChange={setShowMyProfile}>
        <DialogContent className={`max-w-md max-h-[90vh] overflow-x-hidden overflow-y-auto ${
          isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white text-gray-900'
        }`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-pink-400" />
              Mon Profil
              {isPremium && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 border border-yellow-500/30">
                  <Crown className="w-3 h-3 inline mr-1" />
                  Premium
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[70vh]">
            <MyProfileView
              profile={profile}
              customer={customer}
              shadowProfile={null}
              stats={profileStats}
              onEdit={() => { setShowMyProfile(false); setShowSetup(true); }}
              onEditAvatar={() => { setShowMyProfile(false); setShowSetup(true); }}
              onEditFrame={() => { setShowMyProfile(false); setShowSetup(true); }}
              onEditPassions={() => { setShowMyProfile(false); setShowSetup(true); }}
              onOpenShadow={() => { setShowMyProfile(false); setShowShadowMode(true); }}
              onOpenVerification={() => { setShowMyProfile(false); setShowVerificationModal(true); }}
              isDarkMode={isDarkMode}
              crowns={crowns}
            />
            
            {/* 👻 Bouton Paramètres Premium */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <Button
                onClick={() => {
                  setShowMyProfile(false);
                  setShowPremiumSettings(true);
                }}
                className={`w-full ${
                  isPremium 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="btn-premium-settings"
              >
                <Settings className="w-4 h-4 mr-2" />
                {isPremium ? 'Paramètres Premium' : 'Passer Premium'}
                {ghostMode && <Ghost className="w-4 h-4 ml-2 text-violet-300" />}
                {boostActive && <Rocket className="w-4 h-4 ml-2 text-pink-300 animate-bounce" />}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 💬 MODAL ACCROCHE MAGIQUE */}
      <Dialog open={showTaglineEditor} onOpenChange={setShowTaglineEditor}>
        <DialogContent className={`sm:max-w-lg ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-pink-500" />
              Ton accroche magique ✨
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Une phrase qui te définit, qui donne envie de te connaître ! 
              Fais vibrer les cœurs avec tes mots... 💜
            </p>
            
            {/* Champ de saisie */}
            <div className="relative">
              <textarea
                id="tagline-input"
                maxLength={120}
                placeholder="Ex: Le cœur brisé mais toujours prêt à aimer... 💫"
                defaultValue={profile?.tagline || ''}
                className={`w-full p-4 rounded-xl border-2 resize-none h-24 ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                    : 'bg-white border-gray-200 placeholder-gray-400'
                } focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 transition-all`}
              />
              <span className={`absolute bottom-2 right-3 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {(document.getElementById('tagline-input')?.value?.length || profile?.tagline?.length || 0)}/120
              </span>
            </div>
            
            {/* Suggestions créatives */}
            <div>
              <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                ✨ Inspire-toi de ces accroches :
              </h4>
              <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                {[
                  { emoji: '💔', text: "Le cœur brisé, mais je crois encore à l'amour avec un grand A", mood: 'romantique' },
                  { emoji: '🌟', text: "À la recherche de mon étoile dans cette galaxie de rencontres", mood: 'rêveur' },
                  { emoji: '😏', text: "Je ne cherche pas la perfection, juste quelqu'un d'imparfait comme moi", mood: 'authentique' },
                  { emoji: '🔥', text: "Pas là pour jouer, mais pour trouver quelqu'un qui fait battre mon cœur", mood: 'intense' },
                  { emoji: '🦋', text: "Libre comme l'air, mais prête à atterrir pour la bonne personne", mood: 'libre' },
                  { emoji: '☕', text: "Un café, une conversation, et on voit où ça nous mène ?", mood: 'cool' },
                  { emoji: '🎭', text: "Drôle en apparence, profond à l'intérieur", mood: 'mystérieux' },
                  { emoji: '🌈', text: "La vie est trop courte pour les histoires sans saveur", mood: 'positif' },
                  { emoji: '💫', text: "Je ne swipe pas, je choisis. Et si c'était toi ?", mood: 'confiant' },
                  { emoji: '🌙', text: "Âme sensible cherche âme complice pour écrire notre histoire", mood: 'poétique' },
                  { emoji: '🎯', text: "Fini les plans galère, je veux du vrai maintenant", mood: 'direct' },
                  { emoji: '🌺', text: "Fleur rare en quête de son jardin secret", mood: 'délicat' }
                ].map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      const input = document.getElementById('tagline-input');
                      if (input) input.value = suggestion.text;
                    }}
                    className={`text-left p-3 rounded-lg transition-all hover:scale-[1.02] ${
                      isDarkMode 
                        ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700' 
                        : 'bg-gray-50 hover:bg-pink-50 border border-gray-200 hover:border-pink-300'
                    }`}
                  >
                    <span className="text-lg mr-2">{suggestion.emoji}</span>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {suggestion.text}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowTaglineEditor(false)}
              >
                Annuler
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white"
                onClick={async () => {
                  const input = document.getElementById('tagline-input');
                  const newTagline = input?.value?.trim();
                  if (newTagline) {
                    try {
                      const API_URL = process.env.REACT_APP_BACKEND_URL;
                      await fetch(`${API_URL}/api/heartbeats/profile/${customer?.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ tagline: newTagline })
                      });
                      // Mettre à jour le profil localement
                      if (profile) {
                        setProfile({ ...profile, tagline: newTagline });
                      }
                      toast.success('✨ Ton accroche est en ligne !');
                      setShowTaglineEditor(false);
                    } catch (error) {
                      toast.error('Oups, erreur lors de la sauvegarde');
                    }
                  }
                }}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Publier mon accroche
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 👻 MODAL MODE CONFIDENTIEL */}
      <Dialog open={showConfidentialSettings} onOpenChange={setShowConfidentialSettings}>
        <DialogContent className={`sm:max-w-md ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">👻</span>
              Mode Confidentiel
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Active le mode fantôme pour une discrétion totale. Heartbeats disparaît de ton espace visible.
            </p>
            
            {/* Toggle Mode Confidentiel */}
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    🔒 Mode Fantôme
                  </h4>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Heartbeats caché sur le dashboard
                  </p>
                </div>
                <button
                  onClick={() => {
                    const newValue = !isConfidentialMode;
                    setIsConfidentialMode(newValue);
                    localStorage.setItem(`heartbeats_confidential_${customer?.id}`, String(newValue));
                    if (newValue) {
                      toast.success('👻 Mode confidentiel activé ! Triple-tap sur ton avatar pour accéder à Heartbeats');
                    } else {
                      toast.info('🔓 Mode confidentiel désactivé');
                    }
                  }}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    isConfidentialMode 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                      : isDarkMode ? 'bg-gray-700' : 'bg-gray-300'
                  }`}
                >
                  <motion.div
                    animate={{ x: isConfidentialMode ? 26 : 2 }}
                    className="absolute top-1 w-5 h-5 rounded-full bg-white shadow-md"
                  />
                </button>
              </div>
            </div>

            {/* Options quand le mode est activé */}
            {isConfidentialMode && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-3"
              >
                {/* PIN de sécurité */}
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-purple-900/30 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'}`}>
                  <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                    🔐 Code secret (optionnel)
                  </h4>
                  <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Ajoute un code PIN pour plus de sécurité
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="password"
                      maxLength={4}
                      placeholder="4 chiffres"
                      value={confidentialPin}
                      onChange={(e) => setConfidentialPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      className={`flex-1 px-3 py-2 rounded-lg text-center tracking-widest ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 text-white' 
                          : 'bg-white border-gray-300'
                      } border focus:ring-2 focus:ring-purple-500`}
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        if (confidentialPin.length === 4) {
                          localStorage.setItem(`heartbeats_pin_${customer?.id}`, confidentialPin);
                          toast.success('🔐 Code secret enregistré !');
                        } else {
                          toast.error('Le code doit faire 4 chiffres');
                        }
                      }}
                      className="bg-purple-500 text-white"
                    >
                      Sauver
                    </Button>
                  </div>
                </div>

                {/* Infos accès secret */}
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <strong>💡 Accès secret :</strong> Triple-tap sur ton avatar dans l'espace client pour ouvrir Heartbeats discrètement.
                  </p>
                </div>

                {/* Notifications discrètes */}
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🔔 Notifications discrètes
                      </h4>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        "Nouvelle mise à jour" au lieu de "💕 Match !"
                      </p>
                    </div>
                    <Check className="w-5 h-5 text-green-500" />
                  </div>
                </div>
              </motion.div>
            )}

            {/* 🗑️ BOUTON SUPPRESSION DE COMPTE HEARTBEATS (RGPD) */}
            <div className="pt-4 mt-4 border-t border-gray-700">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowConfidentialSettings(false);
                  setShowDeleteAccount(true);
                }}
                className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid="btn-delete-heartbeats-account"
              >
                🗑️ Supprimer mon compte Heartbeats
              </Button>
              <p className={`text-xs text-center mt-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                Supprime uniquement ton profil Heartbeats (rencontres).<br/>
                Ton compte client LPE reste intact.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 👻 PANNEAU PARAMÈTRES PREMIUM (Ghost Mode, Boost, etc.) */}
      <Dialog open={showPremiumSettings} onOpenChange={setShowPremiumSettings}>
        <DialogContent className="bg-gradient-to-b from-[#1a0a2e] to-[#0f0520] border-purple-500/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-400" />
              Paramètres Premium
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Ghost Mode */}
            <div 
              className={`p-4 rounded-xl cursor-pointer transition-all ${
                ghostMode 
                  ? 'bg-violet-500/20 border border-violet-500/50' 
                  : 'bg-gray-800/50 border border-gray-700/50 hover:bg-gray-700/50'
              }`}
              onClick={toggleGhostMode}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Ghost className={`w-6 h-6 ${ghostMode ? 'text-violet-400' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-white">Mode Invisible</p>
                    <p className="text-xs text-gray-400">Vois les profils sans être vu</p>
                  </div>
                </div>
                <div className={`w-12 h-6 rounded-full transition-all ${ghostMode ? 'bg-violet-500' : 'bg-gray-700'}`}>
                  <motion.div
                    className="w-5 h-5 bg-white rounded-full mt-0.5"
                    animate={{ x: ghostMode ? 26 : 2 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                </div>
              </div>
              {ghostMode && (
                <p className="text-xs text-violet-300 mt-2">
                  👻 Tu navigues en mode invisible. Les autres ne te voient pas quand tu regardes leur profil.
                </p>
              )}
            </div>

            {/* Boost */}
            <div 
              className={`p-4 rounded-xl cursor-pointer transition-all ${
                boostActive 
                  ? 'bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/50' 
                  : 'bg-gray-800/50 border border-gray-700/50 hover:bg-gray-700/50'
              }`}
              onClick={activateBoost}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Rocket className={`w-6 h-6 ${boostActive ? 'text-pink-400 animate-bounce' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-white">Boost Profil</p>
                    <p className="text-xs text-gray-400">Sois visible en priorité pendant 30 min</p>
                  </div>
                </div>
                {boostActive ? (
                  <span className="px-2 py-1 rounded-full text-xs font-bold bg-pink-500/30 text-pink-300 animate-pulse">
                    ACTIF
                  </span>
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </div>
              {boostActive && boostEndTime && (
                <p className="text-xs text-pink-300 mt-2">
                  🚀 Boost actif jusqu'à {boostEndTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>

            {/* Super Likes Info */}
            <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50">
              <div className="flex items-center gap-3">
                <Star className="w-6 h-6 text-yellow-400" />
                <div>
                  <p className="font-medium text-white">Super Likes</p>
                  <p className="text-xs text-gray-400">
                    {isPremium ? '5 Super Likes par jour' : '1 Super Like par semaine'}
                  </p>
                </div>
              </div>
            </div>

            {!isPremium && (
              <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30">
                <div className="flex items-center gap-3">
                  <Crown className="w-8 h-8 text-yellow-400" />
                  <div className="flex-1">
                    <p className="font-bold text-white">Passe en Premium</p>
                    <p className="text-xs text-gray-300">Débloque toutes les fonctionnalités exclusives</p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                    onClick={() => {
                      setShowPremiumSettings(false);
                      setShowPaywall({ feature: 'premium' });
                    }}
                  >
                    Upgrade
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 🗑️ MODAL SUPPRESSION DE COMPTE HEARTBEATS (RGPD) */}
      <DeleteAccountModal
        isOpen={showDeleteAccount}
        onClose={() => setShowDeleteAccount(false)}
        customerId={customer?.id}
        onDeleted={() => {
          // Nettoyer UNIQUEMENT les données Heartbeats
          localStorage.removeItem(`heartbeats_token_${customer?.id}`);
          localStorage.removeItem(`heartbeats_age_verified_${customer?.id}`);
          localStorage.removeItem(`heartbeats_rgpd_consent_${customer?.id}`);
          localStorage.removeItem(`heartbeats_confidential_${customer?.id}`);
          localStorage.removeItem(`heartbeats_pin_${customer?.id}`);
          localStorage.removeItem(`heartbeats_vault_unlocked_${customer?.id}`);
          localStorage.removeItem(`heartbeats_profile_${customer?.id}`);
          // Fermer Heartbeats et retourner au portail client
          setShowDeleteAccount(false);
          toast.success('Ton profil Heartbeats a été supprimé. 💜');
          // Callback pour fermer le module
          if (onClose) {
            onClose();
          }
        }}
        isDarkMode={isDarkMode}
        heartbeatsOnly={true}
      />

      {/* 📸 MODAL CRÉATEUR DE STORY */}
      <AnimatePresence>
        {showStoryCreator && (
          <StoryCreator
            onClose={() => setShowStoryCreator(false)}
            onPublish={publishStory}
            isDarkMode={isDarkMode}
          />
        )}
      </AnimatePresence>

      {/* 📸 MODAL VIEWER DE STORY */}
      <AnimatePresence>
        {showStoryViewer && (
          <StoryViewer
            stories={showStoryViewer.stories}
            initialIndex={showStoryViewer.initialIndex}
            onClose={() => setShowStoryViewer(null)}
            onReact={async (storyId, reaction) => {
              try {
                const API_URL = process.env.REACT_APP_BACKEND_URL;
                await axios.post(`${API_URL}/api/stories/${storyId}/react?customer_id=${customer.id}`, { reaction });
              } catch (e) {
                console.error('Erreur réaction:', e);
              }
            }}
            currentUserId={customer?.id}
            isDarkMode={isDarkMode}
          />
        )}
      </AnimatePresence>

      {/* 🔥 MODAL HOT OR NOT */}
      <Dialog open={showHotOrNot} onOpenChange={setShowHotOrNot}>
        <DialogContent className={`sm:max-w-lg ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🔥</span>
              Hot or Not
            </DialogTitle>
          </DialogHeader>
          {hotOrNotPair && hotOrNotPair.length === 2 ? (
            <HotOrNot
              profiles={hotOrNotPair.map(p => ({
                id: p.id,
                name: p.name,
                age: p.age,
                photos: p.photos || (p.avatar ? [p.avatar] : [])
              }))}
              currentUserId={customer?.id}
              onVote={voteHotOrNot}
              isDarkMode={isDarkMode}
            />
          ) : (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p>Pas assez de profils pour jouer...</p>
              <p className="text-sm mt-2">Reviens plus tard ! 🔥</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 💘 MODAL CRUSH SECRET */}
      <Dialog open={!!showSecretCrush} onOpenChange={() => setShowSecretCrush(null)}>
        <DialogContent className={`sm:max-w-md ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          {showSecretCrush && (
            <SecretCrush
              targetProfile={showSecretCrush}
              onSendCrush={sendSecretCrush}
              onClose={() => setShowSecretCrush(null)}
              isDarkMode={isDarkMode}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* 📱 MODAL SHARE CARD */}
      <Dialog open={showShareCard} onOpenChange={setShowShareCard}>
        <DialogContent className={`sm:max-w-md p-0 overflow-y-auto ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          <ShareCard
            profile={{
              id: customer?.id,
              name: profile?.name || customer?.firstName,
              age: profile?.age,
              tagline: profile?.tagline,
              photos: profile?.photos,
              avatar: profile?.avatarImage
            }}
            stats={profileStats}
            onClose={() => setShowShareCard(false)}
            isDarkMode={isDarkMode}
          />
        </DialogContent>
      </Dialog>

      {/* 🎁 MODAL INVITE FRIENDS */}
      <Dialog open={showInviteFriends} onOpenChange={setShowInviteFriends}>
        <DialogContent className={`sm:max-w-md ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          <InviteFriends
            referralCode={referralInfo?.referralCode || `HB-${customer?.id?.substring(0, 6).toUpperCase()}`}
            referralCount={referralInfo?.totalReferred || 0}
            onShare={() => {
              toast.success('Lien copié !');
            }}
            isDarkMode={isDarkMode}
          />
        </DialogContent>
      </Dialog>

      {/* 🎮 MODAL JEU - Tu Préfères ? */}
      <Dialog open={currentGame?.type === 'would-you-rather'} onOpenChange={() => setCurrentGame(null)}>
        <DialogContent className={`sm:max-w-lg ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          <WouldYouRather
            partnerId={currentGame?.partnerId}
            partnerName={currentGame?.partnerName || 'Solo'}
            onAnswer={(questionId, answer) => {
              console.log('Réponse:', questionId, answer);
            }}
            onComplete={() => {
              setCurrentGame(null);
              toast.success('🎮 Partie terminée !');
            }}
            isDarkMode={isDarkMode}
          />
        </DialogContent>
      </Dialog>

      {/* 🎮 MODAL JEU - Quiz Compatibilité */}
      <Dialog open={currentGame?.type === 'compatibility-quiz'} onOpenChange={() => setCurrentGame(null)}>
        <DialogContent className={`sm:max-w-lg ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          <CompatibilityQuiz
            partnerId={currentGame?.partnerId}
            partnerName={currentGame?.partnerName || 'Solo'}
            onComplete={() => {
              setCurrentGame(null);
              toast.success('🧠 Quiz terminé !');
            }}
            isDarkMode={isDarkMode}
          />
        </DialogContent>
      </Dialog>

      {/* 🎮 MODAL GAME LOBBY */}
      <Dialog open={!!showGameLobby} onOpenChange={() => setShowGameLobby(null)}>
        <DialogContent className={`sm:max-w-md ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          {showGameLobby && (
            <GameLobby
              partnerId={showGameLobby.partnerId}
              partnerName={showGameLobby.partnerName}
              onSelectGame={(gameType) => startGame(gameType, showGameLobby.partnerId, showGameLobby.partnerName)}
              onClose={() => setShowGameLobby(null)}
              isDarkMode={isDarkMode}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* 🧚‍♀️ MODAL ICEBREAKERS PIXIE */}
      <Dialog open={!!showIcebreakers} onOpenChange={() => setShowIcebreakers(null)}>
        <DialogContent className={`sm:max-w-md ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          {showIcebreakers && (
            <IcebreakersPanel
              targetProfile={showIcebreakers}
              onSelectIcebreaker={(text) => {
                navigator.clipboard.writeText(text);
                toast.success('🧚‍♀️ Copié ! Colle-le dans le chat !');
              }}
              isDarkMode={isDarkMode}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* 🙈 MODAL REVEAL MOMENT */}
      <AnimatePresence>
        {showRevealMoment && (
          <RevealMoment
            profile={showRevealMoment}
            onContinue={() => setShowRevealMoment(null)}
            isDarkMode={isDarkMode}
          />
        )}
      </AnimatePresence>

      {/* 🌟 MODAL SOUL ASPIRATIONS QUIZ */}
      <Dialog open={showSoulQuiz} onOpenChange={setShowSoulQuiz}>
        <DialogContent className={`sm:max-w-lg max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          <SoulAspirationsQuiz
            customerId={customer?.id}
            onComplete={() => {
              setShowSoulQuiz(false);
              setSoulCompleted(true);
              toast.success('🌟 Aspirations enregistrées !');
            }}
            onSkip={() => setShowSoulQuiz(false)}
            isDarkMode={isDarkMode}
          />
        </DialogContent>
      </Dialog>

      {/* 🧠 MODAL PSYCHE QUIZ */}
      <Dialog open={showPsycheQuiz} onOpenChange={setShowPsycheQuiz}>
        <DialogContent className={`sm:max-w-lg max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          <PsycheQuiz
            customerId={customer?.id}
            onComplete={() => {
              setShowPsycheQuiz(false);
              setPsycheCompleted(true);
              toast.success('✨ Profil enrichi !');
            }}
            onSkip={() => setShowPsycheQuiz(false)}
            isDarkMode={isDarkMode}
          />
        </DialogContent>
      </Dialog>

      {/* ✅ MODAL VÉRIFICATION - Badges de confiance */}
      {showVerificationModal && (
        <VerificationHub
          customerId={customer?.id}
          onComplete={(badge) => {
            // Rafraîchir le profil pour mettre à jour les badges
            fetchProfile();
            toast.success(`${badge === 'email' ? '📧' : '✅'} Badge obtenu !`);
          }}
          onClose={() => setShowVerificationModal(false)}
          isDarkMode={isDarkMode}
          currentBadges={profile?.badges || []}
        />
      )}

      {/* 💰 MODAL AFFILIATION HEARTBEATS PREMIUM */}
      <Dialog open={showAffiliation} onOpenChange={setShowAffiliation}>
        <DialogContent className={`sm:max-w-2xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <TrendingUp className="w-6 h-6 text-emerald-500" />
              Programme Affiliation Premium
            </DialogTitle>
          </DialogHeader>
          <AffiliateDashboard 
            customerId={customer?.id} 
            customerName={customer?.firstName || profile?.displayName}
          />
        </DialogContent>
      </Dialog>

      {/* 🔔 POPUP GUIDES PROACTIFS */}
      <ProactiveGuidePopup
        customerId={customer?.id}
        customerName={customer?.firstName || profile?.displayName}
        onOpenGuideChat={(guideId) => {
          handleTabChange('guides');
        }}
        onAction={(action) => {
          // 🎯 Actions directes depuis les guides proactifs
          switch(action) {
            case 'EDIT_PROFILE':
            case 'ADD_PHOTO':
            case 'ADD_BIO':
            case 'ADD_PASSIONS':
            case 'CREATE_AVATAR':
              setShowSetup(true);
              break;
            default:
              handleTabChange('profile');
          }
        }}
      />

      {/* ⚖️ LIENS LÉGAUX HEARTBEATS - En bas du module, discrets */}
      <HeartbeatsLegalLinks 
        isDarkMode={isDarkMode}
        onOpenCGU={() => setShowHeartbeatsCGU(true)}
        onOpenPrivacy={() => setShowHeartbeatsPrivacy(true)}
      />

      {/* ⚖️ MODAL CGU HEARTBEATS */}
      <HeartbeatsCGUModal 
        isOpen={showHeartbeatsCGU}
        onClose={() => setShowHeartbeatsCGU(false)}
        isDarkMode={isDarkMode}
      />

      {/* 🔒 MODAL POLITIQUE DE CONFIDENTIALITÉ HEARTBEATS */}
      <HeartbeatsPrivacyModal 
        isOpen={showHeartbeatsPrivacy}
        onClose={() => setShowHeartbeatsPrivacy(false)}
        isDarkMode={isDarkMode}
      />
    </div>
    </div>
  );
}

// ============================================
// 💬 Barre de saisie avancée du chat
// ============================================
function ChatInputBar({ isDarkMode, customerId, matchId, onMessageSent, replyingTo, onCancelReply, isWebSocketConnected, onTypingStart, onTypingStop }) {
  const [message, setMessage] = useState('');
  const [isEphemeral, setIsEphemeral] = useState(false);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [photoMaxViews, setPhotoMaxViews] = useState(1);
  const [sending, setSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  // Focus sur l'input quand on répond à un message
  useEffect(() => {
    if (replyingTo && inputRef.current) {
      inputRef.current.focus();
    }
  }, [replyingTo]);
  
  // Envoyer le statut "en train d'écrire"
  const handleTyping = () => {
    // Utiliser WebSocket si disponible, sinon fallback HTTP
    if (isWebSocketConnected && onTypingStart) {
      onTypingStart();
    } else {
      api.setTypingStatus(customerId, matchId, true).catch(console.error);
    }
    
    // Clear le timeout précédent
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Arrêter le typing après 2 secondes d'inactivité
    typingTimeoutRef.current = setTimeout(() => {
      if (isWebSocketConnected && onTypingStop) {
        onTypingStop();
      } else {
        api.setTypingStatus(customerId, matchId, false).catch(console.error);
      }
    }, 2000);
  };
  
  // Cleanup au démontage
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, []);
  
  // Enregistrement vocal
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Audio = reader.result;
          try {
            await api.sendVoiceMessage(customerId, matchId, base64Audio, recordingTime);
            toast.success('🎤 Message vocal envoyé !');
            onMessageSent?.();
          } catch (error) {
            toast.error("Erreur d'envoi du message vocal");
          }
        };
        reader.readAsDataURL(audioBlob);
        
        // Arrêter le stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 60) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (error) {
      toast.error('Impossible d\'accéder au microphone');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };
  
  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setRecordingTime(0);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      toast.info('Enregistrement annulé');
    }
  };
  
  const sendMessage = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    
    try {
      // Envoyer le message avec l'option éphémère et la référence de réponse
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/heartbeats/message/${customerId}`, {
        matchId,
        text: message,
        isEphemeral,
        replyToId: replyingTo?.id || null
      });
      
      setMessage('');
      onCancelReply?.(); // Reset la réponse
      if (isEphemeral) {
        toast.success('👻 Message éphémère envoyé !');
      }
      onMessageSent?.();
    } catch (error) {
      toast.error("Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };
  
  const sendPhoto = async (file) => {
    if (!file) return;
    
    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo trop lourde (max 5MB)');
      return;
    }
    
    setSending(true);
    
    // Convertir en base64
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result;
        await api.sendHeartbeatsPhoto(customerId, matchId, base64, photoMaxViews);
        toast.success(`📷 Photo envoyée ! (${photoMaxViews} vue${photoMaxViews > 1 ? 's' : ''} max)`);
        setShowPhotoOptions(false);
        onMessageSent?.();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Erreur d'envoi");
      } finally {
        setSending(false);
      }
    };
    reader.readAsDataURL(file);
  };
  
  return (
    <div className="mt-4 space-y-2">
      {/* Affichage du message qu'on cite */}
      {replyingTo && (
        <div className={`flex items-center gap-2 p-2 rounded-lg border-l-4 border-pink-500 ${
          isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
        }`}>
          <div className="flex-1 min-w-0">
            <div className={`text-xs font-semibold ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`}>
              ↩️ Réponse à {replyingTo.fromMe ? 'toi-même' : (replyingTo.authorName || 'ton match')}
            </div>
            <div className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {replyingTo.text}
            </div>
          </div>
          <button
            onClick={onCancelReply}
            className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Options message éphémère */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setIsEphemeral(!isEphemeral)}
          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all ${
            isEphemeral 
              ? 'bg-purple-500 text-white' 
              : isDarkMode 
                ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
          title="Message éphémère - disparaît après lecture"
        >
          <span>👻</span>
          <span>{isEphemeral ? 'Éphémère ON' : 'Éphémère'}</span>
        </button>
        
        {isEphemeral && (
          <span className={`text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
            Ce message disparaîtra après lecture
          </span>
        )}
      </div>
      
      {/* Barre de saisie principale */}
      <div className="flex gap-2">
        {/* Bouton Photo avec options */}
        <div className="relative">
          <button 
            onClick={() => setShowPhotoOptions(!showPhotoOptions)}
            className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
          >
            <Camera className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          </button>
          
          {/* Dropdown options photo */}
          {showPhotoOptions && (
            <div className={`absolute bottom-full left-0 mb-2 p-3 rounded-lg shadow-lg z-50 min-w-[200px] ${
              isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
              <p className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                📷 Options de la photo
              </p>
              
              {/* Choix du nombre de vues */}
              <div className="mb-3">
                <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Nombre de vues max :
                </label>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => setPhotoMaxViews(1)}
                    className={`flex-1 py-1 px-2 rounded text-sm ${
                      photoMaxViews === 1 
                        ? 'bg-pink-500 text-white' 
                        : isDarkMode 
                          ? 'bg-gray-700 text-gray-300' 
                          : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    👁️ 1 vue
                  </button>
                  <button
                    onClick={() => setPhotoMaxViews(2)}
                    className={`flex-1 py-1 px-2 rounded text-sm ${
                      photoMaxViews === 2 
                        ? 'bg-pink-500 text-white' 
                        : isDarkMode 
                          ? 'bg-gray-700 text-gray-300' 
                          : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    👁️👁️ 2 vues
                  </button>
                </div>
              </div>
              
              {/* Bouton choisir photo */}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) sendPhoto(file);
                    e.target.value = '';
                  }}
                />
                <div className={`w-full py-2 px-3 rounded-lg text-center text-sm font-medium ${
                  isDarkMode ? 'bg-pink-500 hover:bg-pink-600' : 'bg-pink-500 hover:bg-pink-600'
                } text-white transition-colors`}>
                  📷 Choisir une photo
                </div>
              </label>
              
              <p className={`text-[10px] mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                ⚠️ La photo expire après 24h ou après les vues max
              </p>
            </div>
          )}
        </div>
        
        {/* Mode enregistrement vocal */}
        {isRecording ? (
          <div className="flex-1 flex items-center gap-2">
            <div className={`flex-1 flex items-center gap-3 px-4 py-2 rounded-lg ${
              isDarkMode ? 'bg-red-900/30' : 'bg-red-50'
            }`}>
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className={`text-sm font-medium ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
                {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
              </span>
              <div className="flex-1 flex gap-1">
                {[...Array(20)].map((_, i) => (
                  <div 
                    key={i}
                    className="w-1 bg-red-400 rounded-full animate-pulse"
                    style={{ 
                      height: Math.random() * 16 + 4,
                      animationDelay: `${i * 50}ms`
                    }}
                  />
                ))}
              </div>
            </div>
            <Button
              onClick={cancelRecording}
              variant="ghost"
              className="text-gray-400 hover:text-red-500"
            >
              <X className="w-5 h-5" />
            </Button>
            <Button
              onClick={stopRecording}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Square className="w-4 h-4 fill-white" />
            </Button>
          </div>
        ) : (
          <>
            {/* Input message */}
            <Input
              ref={inputRef}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                handleTyping();
              }}
              placeholder={replyingTo ? "Écris ta réponse..." : isEphemeral ? "Message éphémère 👻..." : "Écris un message..."}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              className={`flex-1 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : ''} ${
                isEphemeral ? 'border-purple-500' : replyingTo ? 'border-pink-500' : ''
              }`}
              disabled={sending}
            />
            
            {/* Bouton micro ou envoyer */}
            {message.trim() ? (
              <Button
                onClick={sendMessage}
                disabled={sending}
                className={`${isEphemeral 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                  : 'bg-gradient-to-r from-purple-500 to-pink-500'
                } text-white`}
              >
                <Send className="w-5 h-5" />
              </Button>
            ) : (
              <Button
                onClick={startRecording}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                title="Enregistrer un message vocal"
              >
                <Mic className="w-5 h-5" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Composant Setup Profil
